// GrindPlanner - v4.14 - Ames Physics Engine
// Tandem geometry simulation - planes to ski line like a planer
// 
// v4.14: Dip detection from ground profile (not ALR-based), improved categorization
// v4.13: Fixed optimizer post-grind ALR extraction, added dip-adjacent to scoring
// Key concepts:
//   - Max Cut / Avg Cut = bump heights removed (not plunge depth)
//   - Continuous Grind = continuous pass, best IRI
//   - Spot Grind = discrete zones, lower material
//
// REFRESH YOUR BROWSER to get this version

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Plot from 'react-plotly.js';
import { 
  ArrowDownTrayIcon, 
  DocumentArrowDownIcon, 
  ArrowUpTrayIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  XCircleIcon, 
  ArrowPathIcon, 
  Cog6ToothIcon, 
  PlayIcon,
  MapIcon,
  ChartBarIcon,
  WrenchScrewdriverIcon,
  InformationCircleIcon,
  ExclamationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import GrindZoneMapTab from './GrindZoneMapTab';

// ============================================================================
// SPINNER COMPONENT
// ============================================================================

function Spinner({ size = 16, className = '' }) {
  return (
    <div 
      className={`inline-block ${className}`}
      style={{
        width: size,
        height: size,
        border: '2px solid rgba(255,255,255,0.3)',
        borderTopColor: 'white',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }}
    />
  );
}

// Add keyframes to document
if (typeof document !== 'undefined' && !document.getElementById('spinner-keyframes')) {
  const style = document.createElement('style');
  style.id = 'spinner-keyframes';
  style.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(style);
}

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

// Quarter-Car Model Parameters (Golden Car - ASTM E1926)
const GOLDEN_CAR = {
  k1: 653.0,    // Tire spring rate (1/s²)
  k2: 63.3,     // Suspension spring rate (1/s²)
  c2: 6.0,      // Damping coefficient (1/s)
  mu: 0.15,     // Sprung/unsprung mass ratio
  V: 80.0/3.6,  // Reference speed: 80 km/h in m/s
};

// IRI Conversion: mm/m to in/mi
const IRI_MM_M_TO_IN_MI = 63.36;

// Machine Archetypes with Full Tandem Geometry
// Based on typical diamond grinding machine configurations
const MACHINE_ARCHETYPES = {
  'urban': { 
    label: 'Urban / Compact (8ft)', 
    wheelbaseFt: 8,
    headPosition: 0.5,        // Head centered
    frontTandemSpreadFt: 2.0,
    rearTandemSpreadFt: 2.0,
    shortCutoffFt: 0.82,      // Butterworth pre-filter
    desc: 'Compact machine for urban streets. High maneuverability, follows surface closely.',
  },
  'standard': { 
    label: 'Standard Highway (12ft)', 
    wheelbaseFt: 12,
    headPosition: 0.5,
    frontTandemSpreadFt: 2.49,
    rearTandemSpreadFt: 2.49,
    shortCutoffFt: 0.82,
    desc: 'Standard 3-4ft head grinder. Best for interstate/highway work.',
  },
  'heavy': { 
    label: 'Heavy Highway (18ft)', 
    wheelbaseFt: 18,
    headPosition: 0.5,
    frontTandemSpreadFt: 2.49,
    rearTandemSpreadFt: 2.49,
    shortCutoffFt: 0.82,
    desc: 'Large frame grinder. Default Ames parameters. Excellent bridging.',
  },
  'airport': { 
    label: 'Airport / Pilot (25ft)', 
    wheelbaseFt: 25,
    headPosition: 0.5,
    frontTandemSpreadFt: 3.0,
    rearTandemSpreadFt: 3.0,
    shortCutoffFt: 1.0,
    desc: 'Super-smooth spec. Maximum bridging for runway/taxiway.',
  },
  'custom': { 
    label: 'Custom Configuration', 
    wheelbaseFt: 12,
    headPosition: 0.5,
    frontTandemSpreadFt: 2.49,
    rearTandemSpreadFt: 2.49,
    shortCutoffFt: 0.82,
    desc: 'Enter specific machine measurements.',
  }
};

// Grinding Strategy Types
const GRIND_STRATEGIES = {
  'continuous': {
    label: 'Continuous Grind',
    shortLabel: 'Continuous',
    desc: 'Planes from first bump to last bump in one continuous pass. Best IRI results, no edge/gap artifacts.',
    icon: '━━━━━━',
    costModel: 'sqyd',
  },
  'spot': {
    label: 'Spot Grind', 
    shortLabel: 'Spot',
    desc: 'Targets only specific bumps with discrete zones. Lower material removed but may create edge artifacts.',
    icon: '━━ ━━ ━━',
    costModel: 'zone',
  },
};

// Default grind settings
const DEFAULT_GRIND_SETTINGS = {
  // Grinding Strategy
  grindStrategy: 'continuous',  // 'continuous' or 'spot'
  
  // Machine Selection
  machineType: 'standard',
  
  // Machine Geometry (populated from archetype)
  wheelbaseFt: 12,
  headPosition: 0.5,
  frontTandemSpreadFt: 2.49,
  rearTandemSpreadFt: 2.49,
  shortCutoffFt: 0.82,
  
  // Simulation
  maxPasses: 5,               // Safety limit on iteration
  
  // Zone Planning (only min zone is user-configurable)
  minZoneLengthFt: 50,        // Min zone length (operational constraint)
  // Lead-in and merge gap are calculated from machine geometry
  
  // Thresholds (typically from project spec)
  iriThreshold: 70,           // Segment IRI pass/fail
  alrThreshold: 70,           // ALR magnitude threshold
  
  // Cost Estimation
  laneWidthFt: 12,
  costPerSqYd: 3.50,          // Continuous grinding rate
  costPerSqYdSpot: 4.50,      // Spot grinding rate
  
  // Polarity Detection
  polarityBaselineFt: 100,    // Lowpass cutoff for baseline
  polarityThresholdIn: 0.03,  // Min deviation to classify
};

/**
 * Calculate physics-based lead-in distance from machine geometry.
 * Lead-in = distance needed for ski line to stabilize before reaching defect.
 * = (wheelbase / 2) + max(front tandem spread, rear tandem spread)
 */
function calculateLeadIn(settings) {
  const { wheelbaseFt = 12, frontTandemSpreadFt = 2.49, rearTandemSpreadFt = 2.49 } = settings;
  const leadIn = (wheelbaseFt / 2) + Math.max(frontTandemSpreadFt, rearTandemSpreadFt);
  return Math.ceil(leadIn); // Round up for safety
}

/**
 * Calculate merge gap from lead-in.
 * If zones are closer than 2× lead-in, their transitions overlap - merge them.
 */
function calculateMergeGap(leadInFt) {
  return leadInFt * 2;
}


// ============================================================================
// BUTTERWORTH FILTERS
// ============================================================================

/**
 * 2nd-order Butterworth Low-Pass Filter (zero-phase)
 * Used for: Baseline extraction, tire/ski smoothing simulation
 */
function applyLowPassFilter(profile, sampleIntervalFt, cutoffFt) {
  const n = profile.length;
  if (n < 10 || cutoffFt <= 0) return new Float32Array(profile);
  
  // Convert cutoff wavelength to frequency
  const fc = 1.0 / cutoffFt;
  const fs = 1.0 / sampleIntervalFt;
  const omega = Math.tan(Math.PI * fc / fs);
  const omega2 = omega * omega;
  const sqrt2 = Math.SQRT2;
  
  // Lowpass coefficients
  const norm = 1.0 / (1.0 + sqrt2 * omega + omega2);
  const b0 = omega2 * norm;
  const b1 = 2.0 * omega2 * norm;
  const b2 = omega2 * norm;
  const a1 = 2.0 * (omega2 - 1.0) * norm;
  const a2 = (1.0 - sqrt2 * omega + omega2) * norm;
  
  // Forward pass
  const forward = new Float32Array(n);
  let x1 = profile[0], x2 = profile[0];
  let y1 = profile[0], y2 = profile[0];
  
  for (let i = 0; i < n; i++) {
    const x0 = profile[i];
    const y0 = b0 * x0 + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
    forward[i] = y0;
    x2 = x1; x1 = x0;
    y2 = y1; y1 = y0;
  }
  
  // Backward pass (zero-phase)
  const result = new Float32Array(n);
  x1 = forward[n-1]; x2 = forward[n-1];
  y1 = forward[n-1]; y2 = forward[n-1];
  
  for (let i = n - 1; i >= 0; i--) {
    const x0 = forward[i];
    const y0 = b0 * x0 + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
    result[i] = y0;
    x2 = x1; x1 = x0;
    y2 = y1; y1 = y0;
  }
  
  return result;
}

/**
 * 2nd-order Butterworth High-Pass Filter (zero-phase)
 * Used for: Removing long-wave grade from profile
 */
function applyHighPassFilter(profile, sampleIntervalFt, cutoffFt = 300.0) {
  const n = profile.length;
  if (n < 10) return new Float32Array(profile);
  
  const fc = 1.0 / cutoffFt;
  const fs = 1.0 / sampleIntervalFt;
  const omega = Math.tan(Math.PI * fc / fs);
  const omega2 = omega * omega;
  const sqrt2 = Math.SQRT2;
  
  // Highpass coefficients
  const norm = 1.0 / (1.0 + sqrt2 * omega + omega2);
  const b0 = norm;
  const b1 = -2.0 * norm;
  const b2 = norm;
  const a1 = 2.0 * (omega2 - 1.0) * norm;
  const a2 = (1.0 - sqrt2 * omega + omega2) * norm;
  
  // Forward pass
  const forward = new Float32Array(n);
  let x1 = profile[0], x2 = profile[0];
  let y1 = 0, y2 = 0;
  
  for (let i = 0; i < n; i++) {
    const x0 = profile[i];
    const y0 = b0 * x0 + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
    forward[i] = y0;
    x2 = x1; x1 = x0;
    y2 = y1; y1 = y0;
  }
  
  // Backward pass (zero-phase)
  const result = new Float32Array(n);
  x1 = forward[n-1]; x2 = forward[n-1];
  y1 = 0; y2 = 0;
  
  for (let i = n - 1; i >= 0; i--) {
    const x0 = forward[i];
    const y0 = b0 * x0 + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
    result[i] = y0;
    x2 = x1; x1 = x0;
    y2 = y1; y1 = y0;
  }
  
  return result;
}


// ============================================================================
// PPF FILE PARSER
// ============================================================================

function parsePPF(arrayBuffer) {
  const view = new DataView(arrayBuffer);
  const decoder = new TextDecoder('utf-8');
  
  // Validate signature
  const signature = decoder.decode(new Uint8Array(arrayBuffer, 0, 4));
  if (signature !== 'SPPF') {
    throw new Error('Invalid PPF file: missing SPPF signature');
  }
  
  const versionStr = decoder.decode(new Uint8Array(arrayBuffer, 4, 4));
  const softwareStr = decoder.decode(new Uint8Array(arrayBuffer, 8, 8));
  const metadataOffset = view.getUint32(16, true);
  const longitudinalOffset = view.getUint32(20, true);
  
  // Parse metadata
  const metadata = parseMetadata(view, arrayBuffer, metadataOffset, longitudinalOffset);
  const numChannels = metadata.numChannels || 2;
  
  // Calculate points from file size if not in metadata
  const dataBytes = arrayBuffer.byteLength - longitudinalOffset;
  const calculatedNumPoints = Math.floor(dataBytes / (numChannels * 4));
  const numPoints = metadata.numPoints || calculatedNumPoints;
  
  // Find sample interval
  let sampleIntervalFt = metadata.sampleInterval;
  if (!sampleIntervalFt || sampleIntervalFt <= 0 || sampleIntervalFt > 1) {
    // Try alternate location
    try {
      const searchVal = view.getFloat32(0x1d0, true);
      if (searchVal > 0.01 && searchVal < 0.5) {
        sampleIntervalFt = searchVal;
      }
    } catch (e) { /* ignore */ }
    sampleIntervalFt = sampleIntervalFt || 0.08202; // Default ~1 inch
  }
  
  const storageFormat = metadata.dataStorageFormat || 2;
  if (numPoints === 0) {
    throw new Error('PPF file has 0 data points');
  }
  
  const sampleIntervalM = sampleIntervalFt * 0.3048;
  
  // Read raw profile data
  const profiles = readProfileData(view, longitudinalOffset, numChannels, numPoints, storageFormat);
  
  // Apply high-pass filter to remove grade
  const leftProfileFiltered = applyHighPassFilter(profiles[0], sampleIntervalFt, 300.0);
  const rightProfileFiltered = profiles[1] 
    ? applyHighPassFilter(profiles[1], sampleIntervalFt, 300.0) 
    : leftProfileFiltered;
  
  const totalLengthFt = (numPoints - 1) * sampleIntervalFt;
  
  // Dynamic lead-in calculation
  const filterWavelength = 300.0;
  const idealLeadIn = filterWavelength * 2;
  const minLeadIn = 100;
  
  let prePostRunFt;
  if (totalLengthFt < 1000) {
    prePostRunFt = Math.max(minLeadIn, totalLengthFt * 0.15);
  } else if (totalLengthFt < 2000) {
    const ratio = (totalLengthFt - 1000) / 1000;
    prePostRunFt = Math.max(minLeadIn, totalLengthFt * 0.15 * (1 - ratio) + idealLeadIn * ratio);
  } else {
    prePostRunFt = idealLeadIn;
  }
  
  const leadInPoints = Math.round(prePostRunFt / sampleIntervalFt);
  const startIdx = leadInPoints;
  const endIdx = numPoints - leadInPoints;
  const analysisPoints = endIdx - startIdx;
  
  if (analysisPoints <= 0) {
    throw new Error('Not enough data points after removing lead-in/out');
  }
  
  // Extract analysis region
  const leftProfileIn = new Float32Array(analysisPoints);
  const rightProfileIn = new Float32Array(analysisPoints);
  const leftProfileM = new Float32Array(analysisPoints);
  const rightProfileM = new Float32Array(analysisPoints);
  
  for (let i = 0; i < analysisPoints; i++) {
    leftProfileIn[i] = leftProfileFiltered[startIdx + i];
    rightProfileIn[i] = rightProfileFiltered[startIdx + i];
    leftProfileM[i] = leftProfileIn[i] * 0.0254;
    rightProfileM[i] = rightProfileIn[i] * 0.0254;
  }
  
  // Extra lead-in for quarter-car settling
  const preSettleFt = 300;
  const preSettlePts = Math.min(startIdx, Math.round(preSettleFt / sampleIntervalFt));
  const preSettleStart = startIdx - preSettlePts;
  
  const leftLeadInM = new Float32Array(preSettlePts);
  const rightLeadInM = new Float32Array(preSettlePts);
  
  for (let i = 0; i < preSettlePts; i++) {
    leftLeadInM[i] = leftProfileFiltered[preSettleStart + i] * 0.0254;
    rightLeadInM[i] = rightProfileFiltered[preSettleStart + i] * 0.0254;
  }
  
  // Generate station array
  const stations = new Float32Array(analysisPoints);
  for (let i = 0; i < analysisPoints; i++) {
    stations[i] = i * sampleIntervalFt;
  }
  
  return {
    header: { signature, version: versionStr, software: softwareStr },
    metadata,
    numChannels,
    numPoints: analysisPoints,
    sampleIntervalFt,
    sampleIntervalM,
    leftProfileIn,
    rightProfileIn,
    leftProfileM,
    rightProfileM,
    leftLeadInM,
    rightLeadInM,
    stations,
    totalLengthFt: analysisPoints * sampleIntervalFt,
    ppfTrimOffset: prePostRunFt,
    preSettlePointsAvailable: preSettlePts,
    rawTotalLengthFt: totalLengthFt,
  };
}

function readProfileData(view, offset, numChannels, numPoints, storageFormat) {
  const profiles = [];
  for (let ch = 0; ch < numChannels; ch++) {
    profiles.push(new Float32Array(numPoints));
  }
  
  let pos = offset;
  
  if (storageFormat === 2) {
    // Channel-sequential
    for (let ch = 0; ch < numChannels; ch++) {
      for (let i = 0; i < numPoints; i++) {
        profiles[ch][i] = view.getFloat32(pos, true);
        pos += 4;
      }
    }
  } else {
    // Point-interleaved
    for (let i = 0; i < numPoints; i++) {
      for (let ch = 0; ch < numChannels; ch++) {
        profiles[ch][i] = view.getFloat32(pos, true);
        pos += 4;
      }
    }
  }
  
  return profiles;
}

function parseMetadata(view, arrayBuffer, start, end) {
  const decoder = new TextDecoder('utf-8');
  const metadata = {};
  let pos = start;
  
  while (pos < end - 4) {
    const tag = view.getUint16(pos, true);
    const len = view.getUint16(pos + 2, true);
    pos += 4;
    
    if (pos + len > end) break;
    
    switch (tag) {
      case 0x0004:
        metadata.numChannels = view.getUint16(pos, true);
        break;
      case 0x0005:
        metadata.numPoints = view.getUint32(pos, true);
        break;
      case 0x0006:
        metadata.sampleInterval = view.getFloat64(pos, true);
        break;
      case 0x0007:
        metadata.beginStation = view.getFloat64(pos, true);
        break;
      case 0x000B:
        metadata.dataStorageFormat = view.getUint16(pos, true);
        break;
      case 0x0001:
        metadata.filename = decoder.decode(new Uint8Array(arrayBuffer, pos, len)).replace(/\0/g, '');
        break;
      case 0x0002:
        metadata.date = decoder.decode(new Uint8Array(arrayBuffer, pos, len)).replace(/\0/g, '');
        break;
      case 0x0003:
        metadata.time = decoder.decode(new Uint8Array(arrayBuffer, pos, len)).replace(/\0/g, '');
        break;
    }
    pos += len;
  }
  
  return metadata;
}


// ============================================================================
// IRI CALCULATION (QUARTER-CAR MODEL)
// ============================================================================

/**
 * Quarter-car state derivatives
 * State: [z_s, z_u, zs_dot, zu_dot]
 * z_s = sprung mass position, z_u = unsprung mass position
 */
function quarterCarDynamics(state, z_r) {
  const { k1, k2, c2, mu } = GOLDEN_CAR;
  const [z_s, z_u, zs_dot, zu_dot] = state;
  
  // Sprung mass acceleration
  const zs_ddot = k2 * (z_u - z_s) + c2 * (zu_dot - zs_dot);
  
  // Unsprung mass acceleration
  const zu_ddot = (k1 * (z_r - z_u) + k2 * (z_s - z_u) + c2 * (zs_dot - zu_dot)) / mu;
  
  return [zs_dot, zu_dot, zs_ddot, zu_ddot];
}

/**
 * 4th-order Runge-Kutta integration step
 */
function rk4Step(state, z_r_current, z_r_next, dt) {
  const z_r_mid = (z_r_current + z_r_next) / 2;
  
  const k1 = quarterCarDynamics(state, z_r_current);
  
  const s2 = [
    state[0] + dt/2 * k1[0],
    state[1] + dt/2 * k1[1],
    state[2] + dt/2 * k1[2],
    state[3] + dt/2 * k1[3]
  ];
  const k2 = quarterCarDynamics(s2, z_r_mid);
  
  const s3 = [
    state[0] + dt/2 * k2[0],
    state[1] + dt/2 * k2[1],
    state[2] + dt/2 * k2[2],
    state[3] + dt/2 * k2[3]
  ];
  const k3 = quarterCarDynamics(s3, z_r_mid);
  
  const s4 = [
    state[0] + dt * k3[0],
    state[1] + dt * k3[1],
    state[2] + dt * k3[2],
    state[3] + dt * k3[3]
  ];
  const k4 = quarterCarDynamics(s4, z_r_next);
  
  return [
    state[0] + dt/6 * (k1[0] + 2*k2[0] + 2*k3[0] + k4[0]),
    state[1] + dt/6 * (k1[1] + 2*k2[1] + 2*k3[1] + k4[1]),
    state[2] + dt/6 * (k1[2] + 2*k2[2] + 2*k3[2] + k4[2]),
    state[3] + dt/6 * (k1[3] + 2*k2[3] + 2*k3[3] + k4[3]),
  ];
}

/**
 * Calculate IRI for a profile segment
 * @param profileM - Profile elevation in meters
 * @param dxM - Sample interval in meters
 * @param preSettlePoints - Number of points to skip for quarter-car settling
 * @returns IRI in in/mi
 */
function calculateIRI(profileM, dxM, preSettlePoints = 0) {
  const n = profileM.length;
  if (n < 2) return 0;
  
  const dt = dxM / GOLDEN_CAR.V;
  
  // Initialize state at road level
  let state = [profileM[0], profileM[0], 0, 0];
  let rectifiedVelocity = 0;
  
  const countStart = Math.min(preSettlePoints, n - 2);
  
  for (let i = 0; i < n - 1; i++) {
    state = rk4Step(state, profileM[i], profileM[i + 1], dt);
    
    // Only accumulate after settling
    if (i >= countStart) {
      // Rectified suspension velocity: |dz_s/dt - dz_u/dt|
      rectifiedVelocity += Math.abs(state[2] - state[3]) * dt;
    }
  }
  
  const countedPoints = n - 1 - countStart;
  const lengthKm = countedPoints * dxM / 1000;
  
  if (lengthKm <= 0) return 0;
  
  // IRI in m/km, convert to in/mi
  return (rectifiedVelocity / lengthKm) * IRI_MM_M_TO_IN_MI;
}

/**
 * Calculate IRI for a specific station range
 */
function calculateRangeIRI(profileIn, sampleIntervalFt, startFt, endFt, preSettleFt = 200) {
  const startIdx = Math.max(0, Math.floor(startFt / sampleIntervalFt));
  const endIdx = Math.min(profileIn.length - 1, Math.ceil(endFt / sampleIntervalFt));
  
  if (endIdx <= startIdx + 10) return 0;
  
  // Include pre-settle region
  const preSettlePts = Math.round(preSettleFt / sampleIntervalFt);
  const sliceStart = Math.max(0, startIdx - preSettlePts);
  const actualPreSettle = startIdx - sliceStart;
  
  // Extract and convert to meters
  const sliceLen = endIdx - sliceStart;
  const profileM = new Float32Array(sliceLen);
  for (let i = 0; i < sliceLen; i++) {
    profileM[i] = profileIn[sliceStart + i] * 0.0254;
  }
  
  const sampleIntervalM = sampleIntervalFt * 0.3048;
  return calculateIRI(profileM, sampleIntervalM, actualPreSettle);
}


// ============================================================================
// CONTINUOUS IRI & ALR DETECTION (Ames ContinuousIriX Style)
// ============================================================================

/**
 * Calculate continuous IRI along a profile using sliding window.
 * Returns array of { station, iri } for each point.
 * 
 * This matches Ames ContinuousIriX algorithm:
 * 1. Slide a window (default 25ft) along the profile
 * 2. Calculate IRI for each window position
 * 3. Store (station, IRI) pairs
 */
function calculateContinuousIRI(profileIn, sampleIntervalFt, baseLengthFt = 25, preSettleFt = 200) {
  const n = profileIn.length;
  const halfBase = baseLengthFt / 2;
  const totalLengthFt = n * sampleIntervalFt;
  
  // Can't calculate if profile shorter than base length
  if (totalLengthFt < baseLengthFt + preSettleFt) {
    return [];
  }
  
  const results = [];
  const stepFt = sampleIntervalFt * 5; // Step ~5 samples for efficiency
  
  // Scan from halfBase to (end - halfBase)
  for (let station = halfBase; station < totalLengthFt - halfBase; station += stepFt) {
    const iri = calculateRangeIRI(profileIn, sampleIntervalFt, 
      station - halfBase, station + halfBase, preSettleFt);
    
    if (!isNaN(iri) && iri > 0) {
      results.push({ station, iri });
    }
  }
  
  return results;
}

/**
 * Find ALR defects in continuous IRI data.
 * Matches Ames ContinuousIriX.FindLocalizedRoughness algorithm:
 * 
 * 1. Scan through (station, IRI) pairs
 * 2. When IRI exceeds threshold, start a defect
 * 3. Track peak IRI and location within defect
 * 4. When IRI drops below threshold, close defect
 * 
 * @returns Array of { startStation, endStation, peakStation, peakIri }
 */
function findALRDefects(continuousIRI, threshold) {
  const defects = [];
  
  let inDefect = false;
  let defectStart = 0;
  let peakIri = 0;
  let peakStation = 0;
  
  for (const { station, iri } of continuousIRI) {
    if (iri > threshold) {
      // Above threshold
      if (!inDefect) {
        // Start new defect
        inDefect = true;
        defectStart = station;
        peakIri = 0;
        peakStation = station;
      }
      
      // Track peak
      if (iri > peakIri) {
        peakIri = iri;
        peakStation = station;
      }
    } else {
      // Below threshold
      if (inDefect) {
        // Close defect
        defects.push({
          startStation: defectStart,
          endStation: station,
          peakStation,
          peakIri,
          length: station - defectStart,
        });
        
        inDefect = false;
      }
    }
  }
  
  // Handle defect extending to end
  if (inDefect && continuousIRI.length > 0) {
    const lastStation = continuousIRI[continuousIRI.length - 1].station;
    defects.push({
      startStation: defectStart,
      endStation: lastStation,
      peakStation,
      peakIri,
      length: lastStation - defectStart,
    });
  }
  
  return defects;
}

/**
 * Calculate peak IRI within a specific region.
 * Used for comparing original ALR regions after grinding.
 */
function calculatePeakIRIInRegion(profileIn, sampleIntervalFt, startFt, endFt, baseLengthFt = 25) {
  const halfBase = baseLengthFt / 2;
  const totalLengthFt = profileIn.length * sampleIntervalFt;
  
  // Clamp scan range to valid area
  const scanStart = Math.max(halfBase, startFt);
  const scanEnd = Math.min(endFt, totalLengthFt - halfBase);
  
  if (scanEnd <= scanStart) {
    // Region too small - calculate at center
    const centerIri = calculateRangeIRI(profileIn, sampleIntervalFt, startFt, endFt);
    return {
      peakIri: centerIri,
      peakStation: (startFt + endFt) / 2,
    };
  }
  
  let peakIri = 0;
  let peakStation = (startFt + endFt) / 2;
  
  // Step through region finding peak (step ~1ft for accuracy)
  const stepFt = Math.max(sampleIntervalFt * 2, 1);
  
  for (let station = scanStart; station <= scanEnd; station += stepFt) {
    const iri = calculateRangeIRI(profileIn, sampleIntervalFt,
      station - halfBase, station + halfBase, 100);
    
    if (iri > peakIri) {
      peakIri = iri;
      peakStation = station;
    }
  }
  
  return { peakIri, peakStation };
}

/**
 * Run full post-grind ALR analysis.
 * This is the proper way to verify correction - same as re-running ProVAL.
 * 
 * @param leftProfile - Left wheelpath ground profile (inches)
 * @param rightProfile - Right wheelpath ground profile (inches)
 * @param sampleIntervalFt - Sample spacing
 * @param threshold - ALR threshold (in/mi)
 * @param stationOffset - System station offset for PPF alignment
 * @returns { leftALRs, rightALRs, allALRs }
 */
function detectPostGrindALRs(leftProfile, rightProfile, sampleIntervalFt, threshold, stationOffset = 0) {
  // Calculate continuous IRI for both wheelpaths
  const leftContinuous = calculateContinuousIRI(leftProfile, sampleIntervalFt, 25, 200);
  const rightContinuous = calculateContinuousIRI(rightProfile, sampleIntervalFt, 25, 200);
  
  // Find ALR defects
  const leftDefects = findALRDefects(leftContinuous, threshold);
  const rightDefects = findALRDefects(rightContinuous, threshold);
  
  // Convert to system stations
  const leftALRs = leftDefects.map((d, i) => ({
    id: `POST-L${i + 1}`,
    wheelpath: 'Left',
    startStation: d.startStation + stationOffset,
    endStation: d.endStation + stationOffset,
    peakStation: d.peakStation + stationOffset,
    magnitude: d.peakIri,
    length: d.length,
  }));
  
  const rightALRs = rightDefects.map((d, i) => ({
    id: `POST-R${i + 1}`,
    wheelpath: 'Right',
    startStation: d.startStation + stationOffset,
    endStation: d.endStation + stationOffset,
    peakStation: d.peakStation + stationOffset,
    magnitude: d.peakIri,
    length: d.length,
  }));
  
  return {
    leftALRs,
    rightALRs,
    allALRs: [...leftALRs, ...rightALRs].sort((a, b) => a.peakStation - b.peakStation),
    leftContinuous,
    rightContinuous,
  };
}


// ============================================================================
// AMES-STYLE GRINDING SIMULATION
// ============================================================================

/**
 * Calculate tandem indices from machine geometry
 * @param samplesPerFt - Profile samples per foot
 * @param wheelbaseFt - Distance between front and rear tandem centers
 * @param headPosition - Grinding head position (0=rear, 1=front, 0.5=center)
 * @param frontSpreadFt - Front tandem axle spread
 * @param rearSpreadFt - Rear tandem axle spread
 */
function calculateTandemIndices(samplesPerFt, wheelbaseFt, headPosition, frontSpreadFt, rearSpreadFt) {
  // Distance from head to each tandem center
  const rearOffset = -wheelbaseFt * headPosition;           // Negative = behind head
  const frontOffset = wheelbaseFt * (1.0 - headPosition);   // Positive = ahead of head
  
  // Convert to sample indices
  return {
    frontTandem0: Math.round((frontOffset - frontSpreadFt / 2.0) * samplesPerFt),
    frontTandem1: Math.round((frontOffset + frontSpreadFt / 2.0) * samplesPerFt),
    rearTandem0: Math.round((rearOffset - rearSpreadFt / 2.0) * samplesPerFt),
    rearTandem1: Math.round((rearOffset + rearSpreadFt / 2.0) * samplesPerFt),
  };
}

/**
 * Apply linear taper to grind depth at zone boundaries
 * Simulates physical ramp of grinder head lowering/lifting
 */
function applySmoothTaper(depthProfile, sampleIntervalFt, taperLengthFt = 25.0) {
  const n = depthProfile.length;
  const result = new Float32Array(depthProfile);
  const taperPoints = Math.round(taperLengthFt / sampleIntervalFt);
  
  // Find all zones (contiguous cut regions)
  const zones = [];
  let inZone = false;
  let zoneStart = 0;
  
  for (let i = 0; i < n; i++) {
    const isCut = depthProfile[i] > 0.001;
    
    if (isCut && !inZone) {
      inZone = true;
      zoneStart = i;
    } else if (!isCut && inZone) {
      inZone = false;
      zones.push({ start: zoneStart, end: i - 1 });
    }
  }
  // Handle zone extending to end
  if (inZone) {
    zones.push({ start: zoneStart, end: n - 1 });
  }
  
  // Apply smooth cosine taper to each zone
  for (const zone of zones) {
    const zoneLen = zone.end - zone.start + 1;
    
    if (zoneLen < 2 * taperPoints) {
      // Short zone - apply bell curve across entire zone
      for (let i = zone.start; i <= zone.end; i++) {
        const relPos = (i - zone.start) / (zoneLen - 1 || 1); // 0 to 1
        // Bell curve: sin(π*x) peaks at 0.5
        const taperFactor = Math.sin(Math.PI * relPos);
        result[i] *= taperFactor;
      }
    } else {
      // Entry taper: smooth S-curve from 0 to 1
      for (let j = 0; j < taperPoints; j++) {
        const t = j / taperPoints; // 0 to 1
        // Smooth S-curve: (1 - cos(π*t)) / 2
        const taperFactor = (1 - Math.cos(Math.PI * t)) / 2;
        const idx = zone.start + j;
        if (idx <= zone.end) {
          result[idx] *= taperFactor;
        }
      }
      
      // Exit taper: smooth S-curve from 1 to 0
      for (let j = 0; j < taperPoints; j++) {
        const t = j / taperPoints; // 0 to 1
        // Smooth S-curve going down
        const taperFactor = (1 - Math.cos(Math.PI * t)) / 2;
        const idx = zone.end - j;
        if (idx >= zone.start + taperPoints) {
          result[idx] *= taperFactor;
        }
      }
    }
  }
  
  return result;
}

/**
 * AMES-STYLE GRINDING SIMULATION
 * 
 * Simulates diamond grinding using tandem bogie geometry:
 * 1. Pre-filter profile with Butterworth lowpass (tire/ski smoothing)
 * 2. At each point, calculate front and rear tandem averages
 * 3. Ski line = average of front and rear tandem elevations
 * 4. Cut ALL material above the ski line (head catches anything protruding)
 * 5. Update filtered profile progressively
 * 6. Apply smooth cosine taper at zone boundaries
 * 
 * NOTE: Per Ames model, cut depth is NOT limited - the head physically
 * catches anything above the ski reference plane. Deep cuts are flagged
 * as warnings but not geometrically limited.
 * 
 * @param profileIn - Raw profile in inches
 * @param sampleIntervalFt - Sample spacing in feet
 * @param machineGeometry - { wheelbaseFt, headPosition, frontTandemSpreadFt, rearTandemSpreadFt, shortCutoffFt }
 * @param mask - Optional Uint8Array, 1 = grind allowed, 0 = skip
 * @param taperLengthFt - Length of smooth taper at zone boundaries (default 25ft)
 */
function applyAmesGrind(profileIn, sampleIntervalFt, machineGeometry, mask = null, taperLengthFt = 25) {
  const n = profileIn.length;
  const samplesPerFt = 1.0 / sampleIntervalFt;
  
  const {
    wheelbaseFt = 18,
    headPosition = 0.5,
    frontTandemSpreadFt = 2.49,
    rearTandemSpreadFt = 2.49,
    shortCutoffFt = 0.82,
  } = machineGeometry;
  
  // Calculate tandem sample indices
  const tandem = calculateTandemIndices(
    samplesPerFt, wheelbaseFt, headPosition, frontTandemSpreadFt, rearTandemSpreadFt
  );
  
  // Pre-filter profile (simulates tire/ski smoothing)
  let tire = applyLowPassFilter(profileIn, sampleIntervalFt, shortCutoffFt);
  
  // Output arrays
  const groundProfile = new Float32Array(profileIn);
  const grindDepth = new Float32Array(n);
  
  // Single pass through profile
  for (let i = 0; i < n; i++) {
    // Skip if outside mask
    if (mask && !mask[i]) continue;
    
    // Bounds check - need full tandem span
    const minIdx = i + tandem.rearTandem0;
    const maxIdx = i + tandem.frontTandem1;
    
    if (minIdx < 0 || maxIdx >= n) {
      // Outside valid range - no grinding
      continue;
    }
    
    // Calculate front tandem average (from filtered profile)
    const frontTandemAvg = (tire[i + tandem.frontTandem0] + tire[i + tandem.frontTandem1]) / 2.0;
    
    // Calculate rear tandem average (from filtered profile)
    const rearTandemAvg = (tire[i + tandem.rearTandem0] + tire[i + tandem.rearTandem1]) / 2.0;
    
    // Ski line elevation at head position
    const skiLineElevation = (frontTandemAvg + rearTandemAvg) / 2.0;
    
    // Original surface elevation
    const originalElevation = profileIn[i];
    
    // Calculate cut (only if surface is above ski line)
    // Per Ames model: cut ALL material above ski line (no depth limit)
    // The head physically catches anything protruding above the reference plane
    if (originalElevation > skiLineElevation + 0.001) {
      const cut = originalElevation - skiLineElevation;
      
      if (cut > 0.001) {
        groundProfile[i] = skiLineElevation;
        grindDepth[i] = cut;
        
        // Update tire array for progressive effect
        tire[i] = skiLineElevation;
      }
    }
  }
  
  // Apply entry/exit tapers (smooth cosine curve)
  const taperedDepth = applySmoothTaper(grindDepth, sampleIntervalFt, taperLengthFt);
  
  // Apply tapered depth to get final ground profile
  const finalGroundProfile = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    finalGroundProfile[i] = profileIn[i] - taperedDepth[i];
  }
  
  return {
    groundProfile: finalGroundProfile,
    grindDepth: taperedDepth,
    rawGrindDepth: grindDepth,
  };
}


// ============================================================================
// POLARITY DETECTION
// ============================================================================

/**
 * Classify bump polarity at a location
 * @param profileIn - Profile in inches
 * @param sampleIntervalFt - Sample spacing
 * @param startFt - Start of region to analyze
 * @param endFt - End of region
 * @param baselineCutoffFt - Lowpass cutoff for baseline (default 100ft)
 * @param thresholdIn - Min deviation to classify (default 0.03")
 * @returns { polarity: 'positive'|'negative'|'mixed', maxPositive, maxNegative, dominantDeviation }
 */
function classifyBumpPolarity(profileIn, sampleIntervalFt, startFt, endFt, baselineCutoffFt = 100, thresholdIn = 0.03) {
  const startIdx = Math.max(0, Math.floor(startFt / sampleIntervalFt));
  const endIdx = Math.min(profileIn.length - 1, Math.ceil(endFt / sampleIntervalFt));
  
  if (endIdx <= startIdx) {
    return { polarity: 'unknown', maxPositive: 0, maxNegative: 0, dominantDeviation: 0 };
  }
  
  // Get baseline using lowpass filter
  const baseline = applyLowPassFilter(profileIn, sampleIntervalFt, baselineCutoffFt);
  
  let maxPositive = 0;
  let maxNegative = 0;
  
  for (let i = startIdx; i <= endIdx; i++) {
    const deviation = profileIn[i] - baseline[i];
    if (deviation > maxPositive) maxPositive = deviation;
    if (deviation < maxNegative) maxNegative = deviation;
  }
  
  const absPositive = Math.abs(maxPositive);
  const absNegative = Math.abs(maxNegative);
  
  // Classification logic:
  // - If there's significant positive content (bumps), it's grindable
  // - Even if there's ALSO a dip, we can still grind the bump
  // - Only classify as "negative" if there's NO grindable bump content
  let polarity;
  if (absPositive > thresholdIn && absNegative > thresholdIn) {
    // Both bump and dip present - grinding will help the bump
    polarity = 'mixed';
  } else if (absPositive > thresholdIn) {
    // Bump only
    polarity = 'positive';
  } else if (absNegative > thresholdIn) {
    // Dip only - nothing to grind
    polarity = 'negative';
  } else {
    polarity = 'flat';
  }
  
  return {
    polarity,
    maxPositive,
    maxNegative,
    dominantDeviation: absPositive > absNegative ? maxPositive : maxNegative,
  };
}

/**
 * Check if an ALR is grindable based on profile polarity
 */
function checkAlrGrindability(alr, ppfData, sessionStartStation, settings) {
  const relPeakStation = alr.peakStation - (sessionStartStation || 0);
  
  // Check if ALR is within PPF range
  if (relPeakStation < 0 || relPeakStation > ppfData.totalLengthFt) {
    return {
      grindable: false,
      reason: 'Outside PPF range',
      polarity: 'unknown',
    };
  }
  
  // Get the profile for this wheelpath
  const profile = alr.wheelpath === 'Left' ? ppfData.leftProfileIn : ppfData.rightProfileIn;
  
  // Analyze region around ALR
  const analysisBuffer = 20; // ft before/after
  const startFt = Math.max(0, relPeakStation - analysisBuffer);
  const endFt = Math.min(ppfData.totalLengthFt, relPeakStation + analysisBuffer);
  
  const polarityResult = classifyBumpPolarity(
    profile,
    ppfData.sampleIntervalFt,
    startFt,
    endFt,
    settings.polarityBaselineFt,
    settings.polarityThresholdIn
  );
  
  if (polarityResult.polarity === 'positive') {
    return {
      grindable: true,
      reason: 'Positive bump (grindable)',
      polarity: 'positive',
      ...polarityResult,
    };
  } else if (polarityResult.polarity === 'negative') {
    return {
      grindable: false,
      reason: 'Negative dip (requires fill/patch)',
      polarity: 'negative',
      ...polarityResult,
    };
  } else if (polarityResult.polarity === 'mixed') {
    return {
      grindable: true,
      reason: 'Mixed polarity (partial improvement expected)',
      polarity: 'mixed',
      ...polarityResult,
    };
  } else {
    return {
      grindable: true,
      reason: 'Flat profile (may not need grinding)',
      polarity: 'flat',
      ...polarityResult,
    };
  }
}


// ============================================================================
// DIP DETECTION FROM PROFILE
// ============================================================================

/**
 * Detect dip locations directly from a profile.
 * Scans for significant negative deviations from baseline.
 * 
 * @param profile - Profile array in inches
 * @param sampleIntervalFt - Sample spacing in feet
 * @param sessionStartStation - Absolute station offset
 * @param baselineCutoffFt - Lowpass cutoff for baseline (default 100ft)
 * @param dipThresholdIn - Minimum negative deviation to count as dip (default 0.05")
 * @param minDipLengthFt - Minimum length to count as dip (default 2ft)
 * @returns Array of { station, depth, startStation, endStation, length }
 */
function detectDipsFromProfile(profile, sampleIntervalFt, sessionStartStation = 0, baselineCutoffFt = 100, dipThresholdIn = 0.05, minDipLengthFt = 2) {
  const n = profile.length;
  if (n < 10) return [];
  
  // Get baseline using lowpass filter
  const baseline = applyLowPassFilter(profile, sampleIntervalFt, baselineCutoffFt);
  
  const dips = [];
  let inDip = false;
  let dipStart = 0;
  let dipMinDeviation = 0;
  let dipMinIdx = 0;
  
  for (let i = 0; i < n; i++) {
    const deviation = profile[i] - baseline[i];
    
    if (deviation < -dipThresholdIn) {
      // We're in a dip
      if (!inDip) {
        // Starting a new dip
        inDip = true;
        dipStart = i;
        dipMinDeviation = deviation;
        dipMinIdx = i;
      } else {
        // Continuing a dip - track deepest point
        if (deviation < dipMinDeviation) {
          dipMinDeviation = deviation;
          dipMinIdx = i;
        }
      }
    } else {
      // Not in a dip
      if (inDip) {
        // Just ended a dip
        const dipLength = (i - dipStart) * sampleIntervalFt;
        if (dipLength >= minDipLengthFt) {
          const startStation = sessionStartStation + dipStart * sampleIntervalFt;
          const endStation = sessionStartStation + i * sampleIntervalFt;
          const peakStation = sessionStartStation + dipMinIdx * sampleIntervalFt;
          
          dips.push({
            station: peakStation,
            startStation,
            endStation,
            length: dipLength,
            depth: dipMinDeviation,  // Negative value in inches
          });
        }
        inDip = false;
      }
    }
  }
  
  // Handle dip that extends to end of profile
  if (inDip) {
    const dipLength = (n - dipStart) * sampleIntervalFt;
    if (dipLength >= minDipLengthFt) {
      const startStation = sessionStartStation + dipStart * sampleIntervalFt;
      const endStation = sessionStartStation + n * sampleIntervalFt;
      const peakStation = sessionStartStation + dipMinIdx * sampleIntervalFt;
      
      dips.push({
        station: peakStation,
        startStation,
        endStation,
        length: dipLength,
        depth: dipMinDeviation,
      });
    }
  }
  
  return dips;
}

/**
 * Detect dips from both wheelpaths of a PPF
 */
function detectAllDipsFromPPF(ppfData, sessionStartStation = 0, dipThresholdIn = 0.05) {
  const leftDips = detectDipsFromProfile(
    ppfData.leftProfileIn,
    ppfData.sampleIntervalFt,
    sessionStartStation,
    100,  // baseline cutoff
    dipThresholdIn,
    2     // min length
  ).map(d => ({ ...d, wheelpath: 'Left' }));
  
  const rightDips = detectDipsFromProfile(
    ppfData.rightProfileIn,
    ppfData.sampleIntervalFt,
    sessionStartStation,
    100,
    dipThresholdIn,
    2
  ).map(d => ({ ...d, wheelpath: 'Right' }));
  
  // Combine and sort by station
  const allDips = [...leftDips, ...rightDips].sort((a, b) => a.station - b.station);
  
  return allDips;
}


// ============================================================================
// GROUND TRUTH ANALYSIS (FROM ACF DATA)
// ============================================================================

function analyzeGroundTruth(segments, events, settings) {
  const { iriThreshold, alrThreshold } = settings;
  
  // Process segments
  const segmentAnalysis = segments
    .filter(s => !s.exception)
    .map((seg, idx) => {
      const startStation = seg.from_station_ft || 0;
      const endStation = seg.to_station_ft || (startStation + 528);
      const mri = seg.iri_avg_in_per_mi || 0;
      
      return {
        segmentNumber: idx + 1,
        segmentId: seg.segment_id,
        startStation,
        endStation,
        length: endStation - startStation,
        mri,
        leftIRI: seg.iri_left_in_per_mi || 0,
        rightIRI: seg.iri_right_in_per_mi || 0,
        status: mri <= iriThreshold ? 'pass' : 'fail',
        delta: mri - iriThreshold,
      };
    });
  
  const failingSegments = segmentAnalysis.filter(s => s.status === 'fail');
  
  // Process ALR events
  const alrEvents = events
    .filter(e => e.event_type === 'LR' && (e.magnitude_in || 0) > alrThreshold)
    .map(e => ({
      eventId: e.event_id,
      wheelpath: e.wheelpath,
      startStation: e.from_station_ft,
      endStation: e.to_station_ft,
      peakStation: e.peak_station_ft || ((e.from_station_ft + e.to_station_ft) / 2),
      magnitude: e.magnitude_in,
      length: (e.to_station_ft || 0) - (e.from_station_ft || 0),
    }));
  
  // Process dips
  const dips = events
    .filter(e => e.event_type === 'Dip')
    .map(e => ({
      eventId: e.event_id,
      wheelpath: e.wheelpath,
      station: e.peak_station_ft || e.from_station_ft,
      magnitude: e.magnitude_in,
      startStation: e.from_station_ft,
      endStation: e.to_station_ft,
    }));
  
  // Associate ALRs with nearby dips (for reference, not exclusion - polarity check handles that)
  const alrsWithDipInfo = alrEvents.map(alr => {
    const nearbyDip = dips.find(d =>
      d.wheelpath === alr.wheelpath &&
      Math.abs(d.station - alr.peakStation) < 50
    );
    return {
      ...alr,
      nearbyDip: nearbyDip || null,
      dipFlag: !!nearbyDip,
    };
  });
  
  // Calculate project MRI
  const totalLength = segmentAnalysis.reduce((sum, s) => sum + s.length, 0);
  const weightedSum = segmentAnalysis.reduce((sum, s) => sum + s.mri * s.length, 0);
  const projectMRI = totalLength > 0 ? weightedSum / totalLength : 0;
  
  return {
    segments: segmentAnalysis,
    failingSegments,
    passingSegments: segmentAnalysis.filter(s => s.status === 'pass'),
    alrEvents: alrsWithDipInfo,
    dips,
    projectMRI,
    complianceRate: segmentAnalysis.length > 0
      ? ((segmentAnalysis.length - failingSegments.length) / segmentAnalysis.length * 100)
      : 100,
    totalSegments: segmentAnalysis.length,
    failCount: failingSegments.length,
    alrCount: alrEvents.length,
  };
}


// ============================================================================
// ZONE IDENTIFICATION
// ============================================================================

/**
 * Identify deficient zones from ground truth, with grindability checks
 * Supports two strategies:
 * - 'continuous': One zone from first defect to last defect (production grinding)
 * - 'spot': Discrete zones around each defect cluster (bump grinding)
 */
function identifyDeficientZones(groundTruth, ppfData, sessionStartStation, settings) {
  const {
    minZoneLengthFt,
    grindStrategy = 'continuous',
    wheelbaseFt = 12,
    frontTandemSpreadFt = 2.49,
    rearTandemSpreadFt = 2.49,
    customMergeGap = false,
    mergeGapFt: customMergeGapFt,
  } = settings;
  
  // Calculate physics-based lead-in from machine geometry
  const leadInFt = calculateLeadIn(settings);
  
  // Use custom merge gap if set, otherwise calculate from lead-in
  const mergeGapFt = customMergeGap && customMergeGapFt ? customMergeGapFt : calculateMergeGap(leadInFt);
  
  
  const offset = sessionStartStation || 0;
  const grindableZones = [];
  const nonGrindableIssues = [];
  const allDipLocations = []; // Track ALL locations with significant negative content
  
  // ========================================================================
  // STEP 1: Identify all grindable ALRs and their extents
  // ========================================================================
  
  const grindableAlrs = [];
  
  for (const alr of groundTruth.alrEvents) {
    // Check grindability using raw profile polarity
    const grindability = checkAlrGrindability(alr, ppfData, sessionStartStation, settings);
    
    // Track dip locations for ANY ALR with significant negative content
    // (even if it's grindable due to mixed polarity)
    if (grindability.maxNegative && Math.abs(grindability.maxNegative) > 0.03) {
      allDipLocations.push({
        station: alr.peakStation,
        magnitude: alr.magnitude,
        eventId: alr.eventId,
        polarity: grindability.polarity,
        negativeDepth: grindability.maxNegative,
      });
    }
    
    if (grindability.grindable) {
      // Use ACF-reported ALR extent directly (no extension needed - lead-in handles it)
      grindableAlrs.push({
        startStation: alr.startStation,
        endStation: alr.endStation,
        peakStation: alr.peakStation,
        triggers: [`ALR ${alr.eventId} (${alr.wheelpath}) ${Math.round(alr.magnitude)} in/mi`],
        type: 'alr',
        targetMagnitude: alr.magnitude,
        wheelpath: alr.wheelpath,
        polarity: grindability.polarity,
        eventId: alr.eventId,
      });
    } else {
      nonGrindableIssues.push({
        ...alr,
        reason: grindability.reason,
        polarity: grindability.polarity,
        recommendation: grindability.polarity === 'negative' ? 'Fill or patch required' : 'Review manually',
      });
    }
  }
  
  // Process failing segments without ALRs (diffuse roughness)
  for (const seg of groundTruth.failingSegments) {
    // Check if any grindable ALRs already cover this segment
    const hasAlrCoverage = grindableAlrs.some(z =>
      z.startStation < seg.endStation && z.endStation > seg.startStation
    );
    
    if (!hasAlrCoverage) {
      // Segment fails but no localized ALRs - check if there's diffuse positive roughness
      const relStart = seg.startStation - offset;
      const relEnd = seg.endStation - offset;
      
      // Only include if within PPF range
      if (relStart < ppfData.totalLengthFt && relEnd > 0) {
        const clampedStart = Math.max(0, relStart);
        const clampedEnd = Math.min(ppfData.totalLengthFt, relEnd);
        
        // Check polarity of the segment
        const leftPolarity = classifyBumpPolarity(
          ppfData.leftProfileIn, ppfData.sampleIntervalFt,
          clampedStart, clampedEnd,
          settings.polarityBaselineFt, settings.polarityThresholdIn
        );
        const rightPolarity = classifyBumpPolarity(
          ppfData.rightProfileIn, ppfData.sampleIntervalFt,
          clampedStart, clampedEnd,
          settings.polarityBaselineFt, settings.polarityThresholdIn
        );
        
        // Include if either wheelpath has positive content
        if (leftPolarity.polarity === 'positive' || rightPolarity.polarity === 'positive' ||
            leftPolarity.polarity === 'mixed' || rightPolarity.polarity === 'mixed') {
          grindableAlrs.push({
            startStation: seg.startStation,
            endStation: seg.endStation,
            peakStation: (seg.startStation + seg.endStation) / 2,
            triggers: [`Segment ${seg.segmentNumber} diffuse roughness (MRI ${seg.mri.toFixed(0)})`],
            type: 'diffuse',
            targetMagnitude: seg.mri,
            polarity: 'mixed',
          });
        } else {
          nonGrindableIssues.push({
            type: 'segment',
            segmentNumber: seg.segmentNumber,
            startStation: seg.startStation,
            endStation: seg.endStation,
            mri: seg.mri,
            reason: 'Failing segment with no grindable positive roughness',
            recommendation: 'Review profile manually - may require reconstruction',
          });
        }
      }
    }
  }
  
  // Sort by start station
  grindableAlrs.sort((a, b) => a.startStation - b.startStation);
  
  // ========================================================================
  // STEP 2: Apply grinding strategy
  // ========================================================================
  
  if (grindStrategy === 'continuous') {
    // ======================
    // CONTINUOUS GRIND MODE
    // ======================
    // Create ONE zone spanning from first defect to last defect
    // Let physics naturally bridge over dips and gaps
    
    if (grindableAlrs.length > 0) {
      const firstDefect = grindableAlrs[0];
      const lastDefect = grindableAlrs[grindableAlrs.length - 1];
      
      const spanStart = firstDefect.startStation - leadInFt;
      const spanEnd = lastDefect.endStation + leadInFt;
      
      // Collect all triggers
      const allTriggers = grindableAlrs.flatMap(z => z.triggers);
      
      // Calculate total span and defect count
      const spanLength = spanEnd - spanStart;
      const defectCount = grindableAlrs.length;
      
      grindableZones.push({
        startStation: spanStart,
        endStation: spanEnd,
        triggers: allTriggers,
        type: 'production',
        targetMagnitude: Math.max(...grindableAlrs.map(z => z.targetMagnitude || 0)),
        polarity: 'mixed',
        strategy: 'continuous',
        spanLength,
        defectCount,
        defectStations: grindableAlrs.map(z => ({
          start: z.startStation,
          end: z.endStation,
          peak: z.peakStation,
          eventId: z.eventId,
        })),
      });
      
    }
    
  } else {
    // ======================
    // SPOT GRIND MODE
    // ======================
    // Create discrete zones around each defect cluster
    // Merge zones if gap is less than calculated merge gap
    
    // Effective merge gap: use calculated value, but at minimum wheelbase
    const effectiveMergeGap = Math.max(mergeGapFt, wheelbaseFt);
    
    // Add lead-in to each zone
    for (const alr of grindableAlrs) {
      grindableZones.push({
        startStation: alr.startStation - leadInFt,
        endStation: alr.endStation + leadInFt,
        triggers: [...alr.triggers],
        type: alr.type,
        targetMagnitude: alr.targetMagnitude,
        wheelpath: alr.wheelpath,
        polarity: alr.polarity,
        strategy: 'spot',
      });
    }
    
    // Iteratively merge and expand until stable
    // This ensures that:
    // 1. Zones within mergeGap get merged
    // 2. Short zones get expanded to minZoneLength
    // 3. If expansion brings zones closer, they get re-merged
    let iterations = 0;
    const maxIterations = 10;
    let changed = true;
    
    while (changed && iterations < maxIterations) {
      changed = false;
      iterations++;
      
      // MERGE PASS: Merge overlapping/adjacent zones
      const mergedZones = [];
      for (const zone of grindableZones) {
        if (mergedZones.length === 0) {
          mergedZones.push({ ...zone, triggers: [...zone.triggers] });
          continue;
        }
        
        const last = mergedZones[mergedZones.length - 1];
        
        if (zone.startStation <= last.endStation + effectiveMergeGap) {
          // Merge
          last.endStation = Math.max(last.endStation, zone.endStation);
          last.triggers.push(...zone.triggers);
          last.targetMagnitude = Math.max(last.targetMagnitude || 0, zone.targetMagnitude || 0);
          if (zone.type === 'alr') last.type = 'alr';
          changed = true;
        } else {
          mergedZones.push({ ...zone, triggers: [...zone.triggers] });
        }
      }
      
      // Replace with merged
      grindableZones.length = 0;
      grindableZones.push(...mergedZones);
      
      // EXPAND PASS: Enforce minimum zone length
      for (const zone of grindableZones) {
        const length = zone.endStation - zone.startStation;
        if (length < minZoneLengthFt) {
          const expand = (minZoneLengthFt - length) / 2;
          zone.startStation -= expand;
          zone.endStation += expand;
          changed = true; // Expansion might bring zones closer, re-check
        }
      }
    }
    
    // Log gap analysis
    if (grindableZones.length > 1) {
      for (let i = 0; i < grindableZones.length - 1; i++) {
        const gap = grindableZones[i + 1].startStation - grindableZones[i].endStation;
      }
    }
  }
  
  return { grindableZones, nonGrindableIssues, grindStrategy, leadInFt, mergeGapFt, allDipLocations };
}


// ============================================================================
// MERGE GAP OPTIMIZATION
// ============================================================================

/**
 * Fast zone planning without full simulation.
 * Used to quickly estimate zone count and coverage for a given merge gap.
 */
function planZonesWithMergeGap(grindableAlrs, leadInFt, mergeGapFt, minZoneLengthFt, wheelbaseFt = 12) {
  if (grindableAlrs.length === 0) return { zones: [], totalLength: 0, zoneCount: 0 };
  
  // Sort ALRs by station
  const sorted = [...grindableAlrs].sort((a, b) => a.startStation - b.startStation);
  
  // Add lead-in to each ALR to create initial zones
  let zones = sorted.map(alr => ({
    startStation: alr.startStation - leadInFt,
    endStation: alr.endStation + leadInFt,
    alrCount: 1,
    alrs: [alr],
  }));
  
  // Effective merge gap (at minimum wheelbase)
  const effectiveMergeGap = Math.max(mergeGapFt, wheelbaseFt);
  
  // Iteratively merge and expand until stable
  let iterations = 0;
  let changed = true;
  
  while (changed && iterations < 10) {
    changed = false;
    iterations++;
    
    // MERGE PASS
    const merged = [];
    for (const zone of zones) {
      if (merged.length === 0) {
        merged.push({ ...zone, alrs: [...zone.alrs] });
        continue;
      }
      
      const last = merged[merged.length - 1];
      if (zone.startStation <= last.endStation + effectiveMergeGap) {
        last.endStation = Math.max(last.endStation, zone.endStation);
        last.alrCount += zone.alrCount;
        last.alrs.push(...zone.alrs);
        changed = true;
      } else {
        merged.push({ ...zone, alrs: [...zone.alrs] });
      }
    }
    zones = merged;
    
    // EXPAND PASS
    for (const zone of zones) {
      const length = zone.endStation - zone.startStation;
      if (length < minZoneLengthFt) {
        const expand = (minZoneLengthFt - length) / 2;
        zone.startStation -= expand;
        zone.endStation += expand;
        changed = true;
      }
    }
  }
  
  // Calculate total length
  const totalLength = zones.reduce((sum, z) => sum + (z.endStation - z.startStation), 0);
  
  return {
    zones,
    zoneCount: zones.length,
    totalLength,
  };
}

/**
 * Generate smart merge gap candidates based on ALR distribution.
 * Returns candidates that span from tight spacing to near-continuous.
 */
function generateMergeGapCandidates(grindableAlrs, leadInFt) {
  if (grindableAlrs.length < 2) return [50];
  
  // Sort ALRs by station
  const sorted = [...grindableAlrs].sort((a, b) => a.peakStation - b.peakStation);
  
  // Calculate gaps between consecutive ALRs
  const gaps = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const gap = sorted[i + 1].startStation - sorted[i].endStation;
    if (gap > 0) gaps.push(gap);
  }
  
  if (gaps.length === 0) return [50];
  
  // Sort gaps
  gaps.sort((a, b) => a - b);
  
  // Generate candidates at key percentiles
  const candidates = new Set([
    leadInFt * 2,  // Physics minimum (2× lead-in)
    50,            // Min zone length
  ]);
  
  // Add percentile-based candidates
  const percentiles = [0.25, 0.5, 0.75, 0.9];
  for (const p of percentiles) {
    const idx = Math.floor(gaps.length * p);
    candidates.add(Math.ceil(gaps[idx] / 10) * 10); // Round to nearest 10
  }
  
  // Add max gap + buffer for near-continuous
  const maxGap = Math.max(...gaps);
  candidates.add(Math.ceil(maxGap * 1.1 / 10) * 10);
  
  // Filter and sort
  const result = [...candidates]
    .filter(g => g >= 18 && g <= 2000)
    .sort((a, b) => a - b);
  
  // Limit to 5 candidates for performance
  if (result.length > 5) {
    const step = Math.floor(result.length / 4);
    return [result[0], result[step], result[step * 2], result[step * 3], result[result.length - 1]];
  }
  
  return result.length > 0 ? result : [50, 100, 200];
}


// ============================================================================
// STATION ALIGNMENT
// ============================================================================

function alignStations(zones, ppfData, sessionStartStation) {
  const offset = sessionStartStation || 0;
  
  return zones.map(zone => {
    const relativeStart = zone.startStation - offset;
    const relativeEnd = zone.endStation - offset;
    
    const ppfStart = Math.max(0, relativeStart);
    const ppfEnd = Math.min(ppfData.totalLengthFt, relativeEnd);
    
    if (ppfEnd <= ppfStart) return null;
    
    return {
      ...zone,
      systemStartStation: zone.startStation,
      systemEndStation: zone.endStation,
      ppfStartStation: ppfStart,
      ppfEndStation: ppfEnd,
      startIdx: Math.max(0, Math.floor(ppfStart / ppfData.sampleIntervalFt)),
      endIdx: Math.min(ppfData.numPoints - 1, Math.ceil(ppfEnd / ppfData.sampleIntervalFt)),
    };
  }).filter(zone => zone !== null);
}


// ============================================================================
// MAIN SIMULATION ORCHESTRATOR
// ============================================================================

function runGrindSimulation(ppfData, alignedZones, settings, groundTruth, sessionStartStation, nonGrindableIssues = [], dipLocations = []) {
  const offset = sessionStartStation || 0;
  
  // Calculate physics-based parameters from machine geometry
  const leadInFt = calculateLeadIn(settings);
  const mergeGapFt = settings.customMergeGap && settings.mergeGapFt ? settings.mergeGapFt : calculateMergeGap(leadInFt);
  
  
  // Build machine geometry from settings
  const machineGeometry = {
    wheelbaseFt: settings.wheelbaseFt,
    headPosition: settings.headPosition,
    frontTandemSpreadFt: settings.frontTandemSpreadFt,
    rearTandemSpreadFt: settings.rearTandemSpreadFt,
    shortCutoffFt: settings.shortCutoffFt,
  };
  
  // Create grind mask from aligned zones
  const mask = new Uint8Array(ppfData.numPoints);
  for (const zone of alignedZones) {
    for (let i = zone.startIdx; i <= zone.endIdx; i++) {
      mask[i] = 1;
    }
  }
  
  // ========================================================================
  // ITERATIVE MULTI-PASS SIMULATION
  // ========================================================================
  
  let leftProfile = new Float32Array(ppfData.leftProfileIn);
  let rightProfile = new Float32Array(ppfData.rightProfileIn);
  
  const leftDepthTotal = new Float32Array(ppfData.numPoints);
  const rightDepthTotal = new Float32Array(ppfData.numPoints);
  
  const passResults = [];
  let passNumber = 0;
  let continueGrinding = true;
  
  // Multi-pass iteration simulates "grind, re-profile, grind again" workflow
  // Since we cut to ski line each pass, subsequent passes typically have
  // minimal additional cutting unless the ski line reference changes
  while (continueGrinding && passNumber < settings.maxPasses) {
    passNumber++;
    
    // Apply one grind pass (cuts to ski line per Ames model)
    const leftGrind = applyAmesGrind(leftProfile, ppfData.sampleIntervalFt, machineGeometry, mask, leadInFt);
    const rightGrind = applyAmesGrind(rightProfile, ppfData.sampleIntervalFt, machineGeometry, mask, leadInFt);
    
    // Accumulate depth
    let passMaxCut = 0;
    let passTotalCut = 0;
    
    for (let i = 0; i < ppfData.numPoints; i++) {
      leftDepthTotal[i] += leftGrind.grindDepth[i];
      rightDepthTotal[i] += rightGrind.grindDepth[i];
      
      const maxCut = Math.max(leftGrind.grindDepth[i], rightGrind.grindDepth[i]);
      if (maxCut > passMaxCut) passMaxCut = maxCut;
      passTotalCut += maxCut;
    }
    
    // Update profiles for next pass
    leftProfile = leftGrind.groundProfile;
    rightProfile = rightGrind.groundProfile;
    
    // Calculate post-pass IRI for each zone
    const zoneIriResults = alignedZones.map(zone => {
      const leftIri = calculateRangeIRI(leftProfile, ppfData.sampleIntervalFt, zone.ppfStartStation, zone.ppfEndStation);
      const rightIri = calculateRangeIRI(rightProfile, ppfData.sampleIntervalFt, zone.ppfStartStation, zone.ppfEndStation);
      return {
        zoneStart: zone.systemStartStation,
        leftIri,
        rightIri,
        avgIri: (leftIri + rightIri) / 2,
        passes: zone.wheelpath === 'Left' ? leftIri <= settings.alrThreshold : 
                zone.wheelpath === 'Right' ? rightIri <= settings.alrThreshold :
                ((leftIri + rightIri) / 2) <= settings.alrThreshold,
      };
    });
    
    passResults.push({
      passNumber,
      maxCut: passMaxCut,
      totalCut: passTotalCut,
      zoneResults: zoneIriResults,
    });
    
    // Check if we should continue
    // Stop if: no significant cutting occurred, or all zones pass threshold
    const allZonesPass = zoneIriResults.every(z => z.passes);
    const negligibleCut = passMaxCut < 0.005;
    
    if (allZonesPass) {
      continueGrinding = false;
    } else if (negligibleCut) {
      continueGrinding = false;
    }
  }
  
  // ========================================================================
  // POST-SIMULATION ANALYSIS
  // ========================================================================
  
  // Detect dips directly from the GROUND profiles (not original)
  // This catches all physical dips regardless of whether they were flagged as ALRs
  const detectedDipsLeft = detectDipsFromProfile(
    leftProfile,
    ppfData.sampleIntervalFt,
    offset,
    100,   // baseline cutoff
    0.04,  // dip threshold (0.04" = ~1mm)
    2      // min length
  ).map(d => ({ ...d, wheelpath: 'Left' }));
  
  const detectedDipsRight = detectDipsFromProfile(
    rightProfile,
    ppfData.sampleIntervalFt,
    offset,
    100,
    0.04,
    2
  ).map(d => ({ ...d, wheelpath: 'Right' }));
  
  const profileDetectedDips = [...detectedDipsLeft, ...detectedDipsRight];
  
  // Convert ground profiles to meters for IRI calculation
  const leftGroundM = new Float32Array(leftProfile.length);
  const rightGroundM = new Float32Array(rightProfile.length);
  for (let i = 0; i < leftProfile.length; i++) {
    leftGroundM[i] = leftProfile[i] * 0.0254;
    rightGroundM[i] = rightProfile[i] * 0.0254;
  }
  
  // Calculate overall IRI
  const leftOrigIRI = calculateIRI(ppfData.leftProfileM, ppfData.sampleIntervalM);
  const rightOrigIRI = calculateIRI(ppfData.rightProfileM, ppfData.sampleIntervalM);
  const calculatedOrigMRI = (leftOrigIRI + rightOrigIRI) / 2;
  
  const leftGroundIRI = calculateIRI(leftGroundM, ppfData.sampleIntervalM);
  const rightGroundIRI = calculateIRI(rightGroundM, ppfData.sampleIntervalM);
  const calculatedGroundMRI = (leftGroundIRI + rightGroundIRI) / 2;
  
  // IRI validation check
  const iriValidationDiff = Math.abs(calculatedOrigMRI - groundTruth.projectMRI);
  const iriValidationMatch = iriValidationDiff < 5;
  const iriValidationWarning = iriValidationDiff >= 5 && iriValidationDiff < 15;
  
  // ========================================================================
  // SEGMENT ANALYSIS
  // ========================================================================
  
  const segmentResults = groundTruth.segments.map(seg => {
    const relStart = seg.startStation - offset;
    const relEnd = seg.endStation - offset;
    
    // Check if segment is in PPF range
    if (relStart < -100 || relEnd > ppfData.totalLengthFt + 100) {
      return {
        ...seg,
        predictedMRI: seg.mri,
        improvement: 0,
        improvementPercent: 0,
        inRange: false,
      };
    }
    
    const leftIri = calculateRangeIRI(leftProfile, ppfData.sampleIntervalFt, Math.max(0, relStart), Math.min(ppfData.totalLengthFt, relEnd));
    const rightIri = calculateRangeIRI(rightProfile, ppfData.sampleIntervalFt, Math.max(0, relStart), Math.min(ppfData.totalLengthFt, relEnd));
    const avgIri = (leftIri + rightIri) / 2;
    
    if (isNaN(avgIri) || avgIri <= 0) {
      return {
        ...seg,
        predictedMRI: seg.mri,
        improvement: 0,
        improvementPercent: 0,
        inRange: true,
      };
    }
    
    return {
      ...seg,
      predictedMRI: avgIri,
      improvement: seg.mri - avgIri,
      improvementPercent: seg.mri > 0 ? ((seg.mri - avgIri) / seg.mri * 100) : 0,
      inRange: true,
    };
  });
  
  // ========================================================================
  // ALR ANALYSIS (Proper Peak Detection)
  // ========================================================================
  
  // First: Run full post-grind ALR detection (same as re-running ProVAL)
  const postGrindALRs = detectPostGrindALRs(
    leftProfile, 
    rightProfile, 
    ppfData.sampleIntervalFt, 
    settings.alrThreshold,
    offset
  );
  
  
  // Now analyze each original ALR
  const alrResults = groundTruth.alrEvents.map(alr => {
    // Check if within PPF range
    const relStart = alr.startStation - offset;
    const relEnd = alr.endStation - offset;
    const relPeak = alr.peakStation - offset;
    
    if (relPeak < 0 || relPeak > ppfData.totalLengthFt) {
      return {
        ...alr,
        predictedMagnitude: alr.magnitude,
        predictedPeakStation: alr.peakStation,
        improvement: 0,
        improvementPercent: 0,
        willPass: false,
        inRange: false,
        wasTargeted: false,
        correctionStatus: 'outside_range',
      };
    }
    
    // Check if this ALR was in a grind zone (targeted for correction)
    const wasTargeted = alignedZones.some(z => 
      alr.peakStation >= z.systemStartStation && 
      alr.peakStation <= z.systemEndStation
    );
    
    // Get the correct wheelpath profile
    const profile = alr.wheelpath === 'Left' ? leftProfile : rightProfile;
    
    // Calculate NEW peak IRI in the original ALR region
    // This is the proper comparison - peak to peak
    const clampedStart = Math.max(0, relStart);
    const clampedEnd = Math.min(ppfData.totalLengthFt, relEnd);
    
    const { peakIri: predictedMagnitude, peakStation: newPeakStation } = calculatePeakIRIInRegion(
      profile,
      ppfData.sampleIntervalFt,
      clampedStart,
      clampedEnd,
      25  // 25ft base to match ACF
    );
    
    // Check if any POST-GRIND ALR overlaps this original ALR location
    // This is the TRUE test - does an ALR still exist here after grinding?
    const overlappingPostALR = postGrindALRs.allALRs.find(postAlr => 
      postAlr.wheelpath === alr.wheelpath &&
      postAlr.startStation < alr.endStation &&
      postAlr.endStation > alr.startStation
    );
    
    // Determine correction status
    let correctionStatus;
    let willPass;
    
    if (!wasTargeted) {
      // Not targeted - check if it happens to be fixed anyway (incidental improvement)
      if (overlappingPostALR) {
        correctionStatus = 'not_targeted_still_defect';
        willPass = false;
      } else if (predictedMagnitude <= settings.alrThreshold) {
        correctionStatus = 'not_targeted_incidental_fix';
        willPass = true;
      } else {
        correctionStatus = 'not_targeted_still_defect';
        willPass = false;
      }
    } else {
      // Was targeted for grinding
      if (overlappingPostALR) {
        correctionStatus = 'targeted_not_fully_corrected';
        willPass = false;
      } else if (predictedMagnitude <= settings.alrThreshold) {
        correctionStatus = 'corrected';
        willPass = true;
      } else {
        // Peak dropped but still above threshold, and no formal ALR detected
        // This can happen at region boundaries
        correctionStatus = 'improved_marginal';
        willPass = predictedMagnitude <= settings.alrThreshold * 1.1; // 10% tolerance
      }
    }
    
    return {
      ...alr,
      predictedMagnitude,
      predictedPeakStation: newPeakStation + offset,
      peakShifted: Math.abs(newPeakStation + offset - alr.peakStation) > 5,
      improvement: alr.magnitude - predictedMagnitude,
      improvementPercent: alr.magnitude > 0 ? ((alr.magnitude - predictedMagnitude) / alr.magnitude * 100) : 0,
      willPass,
      inRange: true,
      wasTargeted,
      correctionStatus,
      overlappingPostALR: overlappingPostALR ? {
        magnitude: overlappingPostALR.magnitude,
        peakStation: overlappingPostALR.peakStation,
      } : null,
    };
  });
  
  // ========================================================================
  // GRIND ZONE EXTRACTION (from actual cut data)
  // ========================================================================
  
  // ========================================================================
  // ZONE DETECTION - Strategy-aware
  // ========================================================================
  
  const grindZones = [];
  const combinedDepth = new Float32Array(ppfData.numPoints);
  for (let i = 0; i < ppfData.numPoints; i++) {
    combinedDepth[i] = Math.max(leftDepthTotal[i], rightDepthTotal[i]);
  }
  
  const grindStrategy = settings.grindStrategy || 'continuous';
  
  if (grindStrategy === 'continuous' && alignedZones.length > 0) {
    // ======================
    // CONTINUOUS MODE: Use planned zone extent
    // ======================
    // In production grinding, we report the full planned extent,
    // even if some areas didn't require cutting (dips, etc.)
    
    // Create single zone from first to last aligned zone
    const firstZone = alignedZones[0];
    const lastZone = alignedZones[alignedZones.length - 1];
    
    const systemStart = firstZone.systemStartStation;
    const systemEnd = lastZone.systemEndStation;
    const ppfStart = firstZone.ppfStartStation;
    const ppfEnd = lastZone.ppfEndStation;
    
    const startIdx = Math.max(0, Math.floor(ppfStart / ppfData.sampleIntervalFt));
    const endIdx = Math.min(ppfData.numPoints - 1, Math.ceil(ppfEnd / ppfData.sampleIntervalFt));
    
    // Calculate cut stats for the zone
    let maxCut = 0, maxCutStation = systemStart;
    let depthSum = 0, depthCount = 0;
    const depthProfile = [];
    
    for (let i = startIdx; i <= endIdx; i++) {
      if (combinedDepth[i] > 0.001) {
        if (combinedDepth[i] > maxCut) {
          maxCut = combinedDepth[i];
          maxCutStation = ppfData.stations[i] + offset;
        }
        depthProfile.push({
          station: ppfData.stations[i] + offset,
          depth: combinedDepth[i],
        });
        depthSum += combinedDepth[i];
        depthCount++;
      }
    }
    
    // Collect all triggers from all aligned zones
    const allTriggers = alignedZones.flatMap(z => z.triggers || []).slice(0, 4);
    
    grindZones.push({
      ppfStartStation: ppfStart,
      ppfEndStation: ppfEnd,
      systemStartStation: systemStart,
      systemEndStation: systemEnd,
      startIdx,
      endIdx,
      length: systemEnd - systemStart,
      maxCut,
      avgCut: depthCount > 0 ? depthSum / depthCount : 0,
      maxCutStation,
      triggers: allTriggers.length > 0 ? allTriggers : ['Continuous grind'],
      depthProfile,
      passesUsed: passNumber,
      strategy: 'continuous',
    });
    
    
  } else {
    // ======================
    // SPOT MODE: Detect from actual grind depth
    // ======================
    // Scan for contiguous grind zones based on where cutting occurred
    
    let inZone = false;
    let zoneStart = 0;
    let zoneMaxCut = 0;
    let zoneMaxStation = 0;
    const gapTolerancePoints = Math.round(5 / ppfData.sampleIntervalFt);
    let gapCounter = 0;
    
    for (let i = 0; i < combinedDepth.length; i++) {
      const depth = combinedDepth[i];
      
      if (depth > 0.001) {
        if (!inZone) {
          inZone = true;
          zoneStart = i;
          zoneMaxCut = 0;
        }
        gapCounter = 0;
        
        if (depth > zoneMaxCut) {
          zoneMaxCut = depth;
          zoneMaxStation = ppfData.stations[i];
        }
      } else if (inZone) {
        gapCounter++;
        
        if (gapCounter > gapTolerancePoints) {
          // Close this zone
          const zoneEnd = i - gapCounter;
          const systemStart = ppfData.stations[zoneStart] + offset;
          const systemEnd = ppfData.stations[zoneEnd] + offset;
          
          // Find matching aligned zones for triggers
          const overlapping = alignedZones.filter(z =>
            !(z.systemEndStation < systemStart || z.systemStartStation > systemEnd)
          );
          const triggers = overlapping.flatMap(t => t.triggers).slice(0, 4);
          
          // Build depth profile for this zone
          const depthProfile = [];
          let depthSum = 0, depthCount = 0;
          for (let j = zoneStart; j <= zoneEnd; j++) {
            if (combinedDepth[j] > 0.001) {
              depthProfile.push({
                station: ppfData.stations[j] + offset,
                depth: combinedDepth[j],
              });
              depthSum += combinedDepth[j];
              depthCount++;
            }
          }
          
          grindZones.push({
            ppfStartStation: ppfData.stations[zoneStart],
            ppfEndStation: ppfData.stations[zoneEnd],
            systemStartStation: systemStart,
            systemEndStation: systemEnd,
            startIdx: zoneStart,
            endIdx: zoneEnd,
            length: systemEnd - systemStart,
            maxCut: zoneMaxCut,
            avgCut: depthCount > 0 ? depthSum / depthCount : 0,
            maxCutStation: zoneMaxStation + offset,
            triggers: triggers.length > 0 ? triggers : ['Geometric grind'],
            depthProfile,
            passesUsed: passNumber,
            strategy: 'spot',
          });
          
          inZone = false;
          gapCounter = 0;
        }
      }
    }
    
    // Handle zone extending to end of file
    if (inZone) {
      const zoneEnd = ppfData.numPoints - 1;
      const systemStart = ppfData.stations[zoneStart] + offset;
      const systemEnd = ppfData.stations[zoneEnd] + offset;
      
      grindZones.push({
        ppfStartStation: ppfData.stations[zoneStart],
        ppfEndStation: ppfData.stations[zoneEnd],
        systemStartStation: systemStart,
        systemEndStation: systemEnd,
        startIdx: zoneStart,
        endIdx: zoneEnd,
        length: systemEnd - systemStart,
        maxCut: zoneMaxCut,
        avgCut: 0,
        maxCutStation: zoneMaxStation + offset,
        triggers: ['End of profile'],
        depthProfile: [],
        passesUsed: passNumber,
        strategy: 'spot',
      });
    }
  }
  
  // Iteratively merge and expand zones until stable
  let finalZones = [...grindZones];
  let iterations = 0;
  const maxIterations = 10;
  let changed = true;
  
  while (changed && iterations < maxIterations) {
    changed = false;
    iterations++;
    
    // MERGE PASS
    const merged = [];
    for (const zone of finalZones) {
      if (merged.length > 0 && zone.strategy === 'spot') {
        const last = merged[merged.length - 1];
        if (zone.systemStartStation - last.systemEndStation <= mergeGapFt) {
          // Merge
          last.ppfEndStation = zone.ppfEndStation;
          last.systemEndStation = zone.systemEndStation;
          last.endIdx = zone.endIdx;
          last.length = last.systemEndStation - last.systemStartStation;
          last.maxCut = Math.max(last.maxCut, zone.maxCut);
          last.depthProfile = [...(last.depthProfile || []), ...(zone.depthProfile || [])];
          last.triggers = [...new Set([...last.triggers, ...zone.triggers])].slice(0, 4);
          changed = true;
          continue;
        }
      }
      merged.push({ ...zone });
    }
    finalZones = merged;
    
    // EXPAND PASS
    for (const zone of finalZones) {
      if (zone.length < settings.minZoneLengthFt) {
        const expand = (settings.minZoneLengthFt - zone.length) / 2;
        zone.ppfStartStation = Math.max(0, zone.ppfStartStation - expand);
        zone.ppfEndStation = Math.min(ppfData.totalLengthFt, zone.ppfEndStation + expand);
        zone.systemStartStation = zone.ppfStartStation + offset;
        zone.systemEndStation = zone.ppfEndStation + offset;
        zone.startIdx = Math.max(0, Math.floor(zone.ppfStartStation / ppfData.sampleIntervalFt));
        zone.endIdx = Math.min(ppfData.numPoints - 1, Math.ceil(zone.ppfEndStation / ppfData.sampleIntervalFt));
        zone.length = zone.systemEndStation - zone.systemStartStation;
        changed = true;
      }
    }
  }
  
  // Assign IDs
  // Per Ames model: simulation cuts to ski line in one pass (like a planer)
  finalZones.forEach((zone, idx) => {
    zone.id = `GZ-${String(idx + 1).padStart(2, '0')}`;
    
    // Sample cut profile for display
    if (zone.depthProfile && zone.depthProfile.length > 100) {
      const step = Math.ceil(zone.depthProfile.length / 100);
      zone.depthProfileSampled = zone.depthProfile.filter((_, i) => i % step === 0);
    } else {
      zone.depthProfileSampled = zone.depthProfile;
    }
  });
  
  // ========================================================================
  // METRICS & COST CALCULATION
  // ========================================================================
  
  const totalGrindLengthFt = finalZones.reduce((sum, z) => sum + z.length, 0);
  const totalAreaSqYd = Math.round(totalGrindLengthFt * settings.laneWidthFt / 9);
  
  let totalDepth = 0, depthCount = 0, maxCutFound = 0;
  for (let i = 0; i < combinedDepth.length; i++) {
    if (combinedDepth[i] > 0.001) {
      totalDepth += combinedDepth[i];
      depthCount++;
      if (combinedDepth[i] > maxCutFound) maxCutFound = combinedDepth[i];
    }
  }
  
  // Cost calculation - simple: area × rate
  const rate = grindStrategy === 'continuous' ? settings.costPerSqYd : settings.costPerSqYdSpot;
  const estimatedCost = Math.round(totalAreaSqYd * rate);
  
  const costBreakdown = {
    grinding: estimatedCost,
    strategy: grindStrategy,
    ratePerSqYd: rate,
  };
  
  // Production estimate (assume ~500 ft/hr for continuous, ~300 ft/hr for spot due to transitions)
  const productionRate = grindStrategy === 'continuous' ? 500 : 300;
  const estimatedHours = (totalGrindLengthFt / productionRate).toFixed(1);
  
  // Summary metrics
  const validSegments = segmentResults.filter(s => s.inRange && s.length > 0);
  const segmentWeightedSum = validSegments.reduce((sum, s) => sum + s.predictedMRI * s.length, 0);
  const segmentTotalLen = validSegments.reduce((sum, s) => sum + s.length, 0);
  const segmentBasedMRI = segmentTotalLen > 0 ? segmentWeightedSum / segmentTotalLen : calculatedGroundMRI;
  
  const predictedProjectMRI = (calculatedGroundMRI > 5 && calculatedGroundMRI < 500)
    ? calculatedGroundMRI
    : segmentBasedMRI;
  
  // Count remaining ALRs properly
  const remainingALRsFromScan = postGrindALRs.allALRs.length;
  const originalALRsStillFailing = alrResults.filter(a => a.inRange && !a.willPass).length;
  const targetedALRs = alrResults.filter(a => a.inRange && a.wasTargeted).length;
  const correctedALRs = alrResults.filter(a => a.inRange && a.correctionStatus === 'corrected').length;
  const notTargetedALRs = alrResults.filter(a => a.inRange && !a.wasTargeted).length;

  // Categorize post-grind ALRs by location relative to grind zones
  // Use PROFILE-DETECTED dips (more accurate than ALR-based dipLocations)
  const categorizedPostALRs = postGrindALRs.allALRs.map(alr => {
    const alrStation = alr.peakStation;
    const alrWheelpath = alr.wheelpath;
    const edgeThreshold = leadInFt; // Within lead-in zone = "edge"
    
    // FIRST: Check if this is near a dip detected in the GROUND profile
    // Check same wheelpath dips with tighter threshold, opposite wheelpath with wider
    let nearbyDip = null;
    const sameWheelpathThreshold = 50;    // ft - tighter for same wheelpath
    const crossWheelpathThreshold = 100;  // ft - wider for cross-wheelpath effects
    
    for (const dip of profileDetectedDips) {
      const distToDip = Math.abs(alrStation - dip.station);
      const threshold = dip.wheelpath === alrWheelpath ? sameWheelpathThreshold : crossWheelpathThreshold;
      
      if (distToDip < threshold) {
        // Prefer same-wheelpath dip if within threshold
        if (!nearbyDip || (dip.wheelpath === alrWheelpath && nearbyDip.wheelpath !== alrWheelpath)) {
          nearbyDip = dip;
        }
      }
    }
    
    // Also check ALR-based dip locations as backup (original non-grindable dips)
    if (!nearbyDip) {
      for (const dip of dipLocations) {
        const distToDip = Math.abs(alrStation - dip.station);
        if (distToDip < 100) {
          nearbyDip = dip;
          break;
        }
      }
    }
    
    // If near a dip, classify as dip_adjacent regardless of zone position
    if (nearbyDip) {
      return {
        ...alr,
        relation: 'dip_adjacent',
        closestZone: null,
        distanceToZone: Infinity,
        nearbyDip: nearbyDip.eventId || `profile_${nearbyDip.station?.toFixed(0)}`,
        dipDepth: nearbyDip.depth,
      };
    }
    
    // Find closest zone
    let closestZone = null;
    let minDistance = Infinity;
    let relation = 'uncovered';
    
    for (const zone of finalZones) {
      if (alrStation >= zone.systemStartStation && alrStation <= zone.systemEndStation) {
        // Inside a zone
        relation = 'inside_zone';
        closestZone = zone;
        minDistance = 0;
        break;
      }
      
      // Check distance to zone boundaries
      const distToStart = Math.abs(alrStation - zone.systemStartStation);
      const distToEnd = Math.abs(alrStation - zone.systemEndStation);
      const minDist = Math.min(distToStart, distToEnd);
      
      if (minDist < minDistance) {
        minDistance = minDist;
        closestZone = zone;
      }
    }
    
    if (relation !== 'inside_zone') {
      if (minDistance <= edgeThreshold) {
        relation = 'edge_artifact';
      } else {
        // Check if between two zones (gap)
        for (let i = 0; i < finalZones.length - 1; i++) {
          const zone1End = finalZones[i].systemEndStation;
          const zone2Start = finalZones[i + 1].systemStartStation;
          if (alrStation > zone1End && alrStation < zone2Start) {
            relation = 'gap_issue';
            break;
          }
        }
      }
    }
    
    return {
      ...alr,
      relation,
      closestZone: closestZone?.id || null,
      distanceToZone: minDistance,
      nearbyDip: null,
    };
  });
  
  // Count by category
  const edgeArtifacts = categorizedPostALRs.filter(a => a.relation === 'edge_artifact').length;
  const gapIssues = categorizedPostALRs.filter(a => a.relation === 'gap_issue').length;
  const dipAdjacent = categorizedPostALRs.filter(a => a.relation === 'dip_adjacent').length;
  const uncovered = categorizedPostALRs.filter(a => a.relation === 'uncovered').length;

  return {
    // Ground profiles
    leftGroundProfile: leftProfile,
    rightGroundProfile: rightProfile,
    leftGrindDepth: leftDepthTotal,
    rightGrindDepth: rightDepthTotal,
    
    // Zones
    grindZones: finalZones,
    
    // Analysis results
    segmentResults,
    alrResults,
    passResults,
    totalPassesUsed: passNumber,
    
    // Post-grind ALR detection (like re-running ProVAL)
    postGrindALRs,
    
    // Metrics
    metrics: {
      totalGrindLengthFt,
      totalAreaSqYd,
      avgCutIn: depthCount > 0 ? totalDepth / depthCount : 0,
      maxCutIn: maxCutFound,
      estimatedCost,
      costBreakdown,
      estimatedHours,
      totalZones: finalZones.length,
      grindStrategy,
    },
    
    // Summary
    summary: {
      originalProjectMRI: groundTruth.projectMRI,
      calculatedOriginalMRI: calculatedOrigMRI,
      predictedProjectMRI,
      iriImprovement: groundTruth.projectMRI - predictedProjectMRI,
      iriImprovementPercent: ((groundTruth.projectMRI - predictedProjectMRI) / groundTruth.projectMRI * 100),
      originalFailingSegments: groundTruth.failCount,
      remainingFailingSegments: segmentResults.filter(s => s.inRange && s.predictedMRI > settings.iriThreshold).length,
      
      // ALR Summary (corrected methodology)
      originalALRCount: groundTruth.alrCount,
      targetedALRs,
      correctedALRs,
      notTargetedALRs,
      remainingALRsFromScan,  // ALRs detected in post-grind profile scan
      remainingALRsFromOriginal: originalALRsStillFailing,
      
      // Post-grind ALR categorization
      postGrindEdgeArtifacts: edgeArtifacts,
      postGrindGapIssues: gapIssues,
      postGrindUncovered: uncovered,
      
      originalComplianceRate: groundTruth.complianceRate,
      predictedComplianceRate: validSegments.length > 0
        ? ((validSegments.length - validSegments.filter(s => s.predictedMRI > settings.iriThreshold).length) / validSegments.length * 100)
        : 100,
      iriValidationMatch,
      iriValidationWarning,
      iriValidationDiff,
    },
    
    // Issues
    remainingIssues: {
      segments: segmentResults.filter(s => s.inRange && s.predictedMRI > settings.iriThreshold),
      alrs: alrResults.filter(a => a.inRange && !a.willPass),
      postGrindALRs: categorizedPostALRs,
    },
  };
}


// ============================================================================
// SETTINGS DROPDOWN COMPONENT
// ============================================================================

function SettingsDropdown({
  isOpen,
  onClose,
  anchorRef,
  grindSettings,
  setGrindSettings,
}) {
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Handle machine type change
  const handleMachineChange = (type) => {
    const archetype = MACHINE_ARCHETYPES[type];
    if (!archetype) return;

    setGrindSettings(prev => ({
      ...prev,
      machineType: type,
      wheelbaseFt: archetype.wheelbaseFt,
      headPosition: archetype.headPosition,
      frontTandemSpreadFt: archetype.frontTandemSpreadFt,
      rearTandemSpreadFt: archetype.rearTandemSpreadFt,
      shortCutoffFt: archetype.shortCutoffFt,
    }));
  };

  // Position dropdown
  useEffect(() => {
    if (isOpen && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: Math.max(8, rect.right - 360),
      });
    }
  }, [isOpen, anchorRef]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          anchorRef.current && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  const currentArchetype = MACHINE_ARCHETYPES[grindSettings.machineType] || MACHINE_ARCHETYPES['standard'];

  return ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className="fixed bg-white rounded-lg shadow-xl border border-gray-200 p-4 z-50 w-[400px] max-h-[80vh] overflow-y-auto"
      style={{ top: position.top, left: position.left }}
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-gray-900">Simulation Settings</h4>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <XCircleIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-4">
        {/* Machine Configuration */}
        <div className="bg-blue-50 p-3 rounded border border-blue-200">
          <label className="block text-xs font-bold text-blue-800 mb-1.5">
            Machine Configuration
          </label>
          <select
            value={grindSettings.machineType}
            onChange={(e) => handleMachineChange(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-blue-300 rounded mb-2"
          >
            {Object.entries(MACHINE_ARCHETYPES).map(([key, data]) => (
              <option key={key} value={key}>{data.label}</option>
            ))}
          </select>
          <p className="text-[10px] text-blue-600 italic leading-tight">
            {currentArchetype.desc}
          </p>

          {/* Geometry display/edit */}
          <div className="mt-2 pt-2 border-t border-blue-200 grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-blue-700">Wheelbase</label>
              <div className="flex items-center">
                <input
                  type="number"
                  value={grindSettings.wheelbaseFt}
                  onChange={(e) => setGrindSettings(prev => ({ ...prev, wheelbaseFt: parseFloat(e.target.value) || 12 }))}
                  disabled={grindSettings.machineType !== 'custom'}
                  className={`w-full px-2 py-1 text-xs border rounded ${grindSettings.machineType !== 'custom' ? 'bg-blue-100 text-blue-600' : 'bg-white'}`}
                />
                <span className="ml-1 text-[10px] text-blue-500">ft</span>
              </div>
            </div>
            <div>
              <label className="text-[10px] text-blue-700">Head Position</label>
              <div className="flex items-center">
                <input
                  type="number"
                  step="0.1"
                  value={grindSettings.headPosition}
                  onChange={(e) => setGrindSettings(prev => ({ ...prev, headPosition: parseFloat(e.target.value) || 0.5 }))}
                  disabled={grindSettings.machineType !== 'custom'}
                  className={`w-full px-2 py-1 text-xs border rounded ${grindSettings.machineType !== 'custom' ? 'bg-blue-100 text-blue-600' : 'bg-white'}`}
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-blue-700">Front Tandem</label>
              <div className="flex items-center">
                <input
                  type="number"
                  step="0.1"
                  value={grindSettings.frontTandemSpreadFt}
                  onChange={(e) => setGrindSettings(prev => ({ ...prev, frontTandemSpreadFt: parseFloat(e.target.value) || 2.49 }))}
                  disabled={grindSettings.machineType !== 'custom'}
                  className={`w-full px-2 py-1 text-xs border rounded ${grindSettings.machineType !== 'custom' ? 'bg-blue-100 text-blue-600' : 'bg-white'}`}
                />
                <span className="ml-1 text-[10px] text-blue-500">ft</span>
              </div>
            </div>
            <div>
              <label className="text-[10px] text-blue-700">Rear Tandem</label>
              <div className="flex items-center">
                <input
                  type="number"
                  step="0.1"
                  value={grindSettings.rearTandemSpreadFt}
                  onChange={(e) => setGrindSettings(prev => ({ ...prev, rearTandemSpreadFt: parseFloat(e.target.value) || 2.49 }))}
                  disabled={grindSettings.machineType !== 'custom'}
                  className={`w-full px-2 py-1 text-xs border rounded ${grindSettings.machineType !== 'custom' ? 'bg-blue-100 text-blue-600' : 'bg-white'}`}
                />
                <span className="ml-1 text-[10px] text-blue-500">ft</span>
              </div>
            </div>
          </div>
        </div>

        {/* Zone Planning */}
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Min Zone (ft)</label>
              <input
                type="number"
                value={grindSettings.minZoneLengthFt}
                onChange={(e) => setGrindSettings(prev => ({ ...prev, minZoneLengthFt: parseInt(e.target.value) || 50 }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Lane Width (ft)</label>
              <input
                type="number"
                value={grindSettings.laneWidthFt}
                onChange={(e) => setGrindSettings(prev => ({ ...prev, laneWidthFt: parseInt(e.target.value) || 12 }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
              />
            </div>
          </div>
          {/* Calculated lead-in and merge gap input */}
          <div className="text-xs bg-gray-50 px-2 py-1.5 rounded space-y-1.5">
            <div className="text-gray-500">
              Lead-in: {Math.ceil((grindSettings.wheelbaseFt / 2) + Math.max(grindSettings.frontTandemSpreadFt, grindSettings.rearTandemSpreadFt))}ft
              <span className="text-gray-400 ml-1">(from geometry)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Merge Gap:</span>
              <input
                type="text"
                value={grindSettings.customMergeGap ? grindSettings.mergeGapFt : ''}
                placeholder="Auto"
                onChange={(e) => {
                  const val = e.target.value.trim();
                  if (val === '' || val.toLowerCase() === 'auto') {
                    setGrindSettings(prev => ({ ...prev, customMergeGap: false, mergeGapFt: undefined }));
                  } else {
                    const num = parseInt(val);
                    if (!isNaN(num) && num > 0) {
                      setGrindSettings(prev => ({ ...prev, customMergeGap: true, mergeGapFt: num }));
                    }
                  }
                }}
                className="w-20 px-2 py-1 text-xs border border-gray-300 rounded text-center"
              />
              <span className="text-gray-400">ft</span>
            </div>
            <div className="text-[10px] text-gray-400">
              {grindSettings.customMergeGap 
                ? 'Using custom override' 
                : 'Auto-optimizes for spot grinding'}
            </div>
          </div>
        </div>

        {/* Cost Settings - Both Strategies */}
        <div className="bg-gray-50 p-2 rounded border border-gray-200">
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Cost Estimation
          </label>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">$/SY (Continuous)</label>
              <input
                type="number"
                step="0.25"
                value={grindSettings.costPerSqYd}
                onChange={(e) => setGrindSettings(prev => ({ ...prev, costPerSqYd: parseFloat(e.target.value) || 3.5 }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">$/SY (Spot)</label>
              <input
                type="number"
                step="0.25"
                value={grindSettings.costPerSqYdSpot}
                onChange={(e) => setGrindSettings(prev => ({ ...prev, costPerSqYdSpot: parseFloat(e.target.value) || 4.5 }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
              />
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}


// ============================================================================
// PROFILE CHART COMPONENT
// ============================================================================

const ProfileChart = ({ ppfData, simulationResult, grindZones, sessionStartStation = 0 }) => {
  const [wheelpath, setWheelpath] = useState('both');
  const [viewWidthFt, setViewWidthFt] = useState(100);
  const [viewStation, setViewStation] = useState(0);

  const stations = ppfData?.stations;
  const sampleIntervalFt = ppfData?.sampleIntervalFt || 0.0820;
  const stationOffset = sessionStartStation || 0;
  const totalLengthFt = ppfData?.totalLengthFt || 0;

  const { plotData, plotLayout, plotConfig } = useMemo(() => {
    if (!ppfData || !simulationResult || !stations) {
      return { plotData: [], plotLayout: {}, plotConfig: {} };
    }

    const leftOriginal = ppfData.leftProfileIn;
    const rightOriginal = ppfData.rightProfileIn;
    const leftGround = simulationResult.leftGroundProfile;
    const rightGround = simulationResult.rightGroundProfile;

    const startIdx = Math.max(0, Math.floor(viewStation / sampleIntervalFt));
    const endIdx = Math.min(leftOriginal.length - 1, startIdx + Math.ceil(viewWidthFt / sampleIntervalFt));

    if (endIdx <= startIdx || startIdx >= stations.length) {
      return { plotData: [], plotLayout: {}, plotConfig: {} };
    }

    const viewStartSystemStation = stations[startIdx] + stationOffset;
    const viewEndSystemStation = stations[endIdx] + stationOffset;

    const maxPoints = 1500;
    const step = Math.max(1, Math.floor((endIdx - startIdx) / maxPoints));

    const xData = [], leftOrigData = [], rightOrigData = [], leftGroundData = [], rightGroundData = [];
    for (let i = startIdx; i <= endIdx; i += step) {
      xData.push(stations[i] + stationOffset);
      leftOrigData.push(leftOriginal[i]);
      rightOrigData.push(rightOriginal[i]);
      leftGroundData.push(leftGround[i]);
      rightGroundData.push(rightGround[i]);
    }

    const visibleZones = (grindZones || []).filter(zone =>
      zone.systemEndStation >= viewStartSystemStation && zone.systemStartStation <= viewEndSystemStation
    );

    const shapes = visibleZones.map(zone => ({
      type: 'rect',
      xref: 'x',
      yref: 'paper',
      x0: Math.max(zone.systemStartStation, viewStartSystemStation),
      x1: Math.min(zone.systemEndStation, viewEndSystemStation),
      y0: 0,
      y1: 1,
      fillcolor: 'rgba(239, 68, 68, 0.12)',
      line: { width: 0 }
    }));

    const annotations = visibleZones.map(zone => {
      const zoneStart = Math.max(zone.systemStartStation, viewStartSystemStation);
      const zoneEnd = Math.min(zone.systemEndStation, viewEndSystemStation);
      const zoneMid = (zoneStart + zoneEnd) / 2;
      return {
        x: zoneMid,
        y: 1,
        xref: 'x',
        yref: 'paper',
        text: zone.id,
        showarrow: false,
        font: { color: '#ef4444', size: 11, family: 'Arial Black' },
        bgcolor: 'rgba(0,0,0,0.7)',
        borderpad: 3,
        yanchor: 'top',
        yshift: -5,
      };
    });

    const traces = [];

    if (wheelpath === 'left' || wheelpath === 'both') {
      traces.push({
        x: [...xData, ...xData.slice().reverse()],
        y: [...leftOrigData, ...leftGroundData.slice().reverse()],
        type: 'scatter',
        mode: 'none',
        name: 'Left Removed',
        fill: 'toself',
        fillcolor: 'rgba(239, 68, 68, 0.3)',
        hoverinfo: 'skip',
        showlegend: wheelpath === 'left'
      });
      traces.push({
        x: xData,
        y: leftOrigData,
        type: 'scatter',
        mode: 'lines',
        name: 'Left Original',
        line: { color: '#9ca3af', width: 1.5, dash: wheelpath === 'both' ? 'dot' : 'solid' }
      });
      traces.push({
        x: xData,
        y: leftGroundData,
        type: 'scatter',
        mode: 'lines',
        name: 'Left Ground',
        line: { color: '#3b82f6', width: 2 }
      });
    }

    if (wheelpath === 'right' || wheelpath === 'both') {
      traces.push({
        x: [...xData, ...xData.slice().reverse()],
        y: [...rightOrigData, ...rightGroundData.slice().reverse()],
        type: 'scatter',
        mode: 'none',
        name: 'Right Removed',
        fill: 'toself',
        fillcolor: 'rgba(34, 197, 94, 0.25)',
        hoverinfo: 'skip',
        showlegend: wheelpath === 'right'
      });
      traces.push({
        x: xData,
        y: rightOrigData,
        type: 'scatter',
        mode: 'lines',
        name: 'Right Original',
        line: { color: '#737373', width: 1.5, dash: wheelpath === 'both' ? 'dot' : 'solid' }
      });
      traces.push({
        x: xData,
        y: rightGroundData,
        type: 'scatter',
        mode: 'lines',
        name: 'Right Ground',
        line: { color: '#22c55e', width: 2 }
      });
    }

    const layout = {
      paper_bgcolor: '#111111',
      plot_bgcolor: '#0a0a0a',
      margin: { l: 60, r: 20, t: 30, b: 50 },
      height: 300,
      showlegend: true,
      legend: { x: 1, xanchor: 'right', y: 1, bgcolor: 'rgba(0,0,0,0.6)', font: { color: '#9ca3af', size: 10 } },
      xaxis: {
        title: { text: 'Station (ft)', font: { color: '#9ca3af', size: 11 } },
        color: '#737373',
        gridcolor: '#262626',
        zerolinecolor: '#333333',
        tickformat: '.0f',
        range: [viewStartSystemStation, viewEndSystemStation]
      },
      yaxis: {
        title: { text: 'Elevation (in)', font: { color: '#9ca3af', size: 11 } },
        color: '#737373',
        gridcolor: '#262626',
        zerolinecolor: '#333333'
      },
      shapes,
      annotations,
      hovermode: 'x unified',
    };

    return {
      plotData: traces,
      plotLayout: layout,
      plotConfig: {
        responsive: true,
        displayModeBar: true,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
        displaylogo: false
      }
    };
  }, [ppfData, simulationResult, stations, viewStation, viewWidthFt, grindZones, sampleIntervalFt, wheelpath, stationOffset]);

  if (!ppfData || !simulationResult) {
    return <div className="text-gray-500 text-center py-8">Run simulation to view profile</div>;
  }

  const moveView = (delta) => {
    const maxStation = Math.max(0, totalLengthFt - viewWidthFt);
    setViewStation(prev => Math.max(0, Math.min(maxStation, prev + delta)));
  };

  const toHighwayStation = (ft) => {
    const sta = Math.floor(ft / 100);
    const offset = (ft % 100).toFixed(0);
    return `${sta}+${offset.padStart(2, '0')}`;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div className="inline-flex rounded overflow-hidden border border-gray-200">
          {[
            { key: 'left', label: 'L', color: 'bg-blue-500' },
            { key: 'both', label: 'Both', color: 'bg-purple-500' },
            { key: 'right', label: 'R', color: 'bg-green-500' }
          ].map(({ key, label, color }) => (
            <button
              key={key}
              onClick={() => setWheelpath(key)}
              className={`px-3 py-1 text-xs font-medium transition-colors ${wheelpath === key ? `${color} text-white` : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {viewWidthFt < totalLengthFt && (
            <div className="inline-flex rounded overflow-hidden border border-gray-200">
              <button
                onClick={() => moveView(-viewWidthFt * 0.25)}
                disabled={viewStation <= 0}
                className="p-1.5 bg-white hover:bg-gray-50 disabled:opacity-30"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => moveView(viewWidthFt * 0.25)}
                disabled={viewStation >= totalLengthFt - viewWidthFt}
                className="p-1.5 bg-white hover:bg-gray-50 disabled:opacity-30 border-l border-gray-200"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
          <div className="inline-flex rounded overflow-hidden border border-gray-200">
            {[100, 250, 500, 1000].map((w) => (
              <button
                key={w}
                onClick={() => setViewWidthFt(w)}
                className={`px-2 py-1 text-xs transition-colors ${viewWidthFt === w ? 'bg-gray-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                {w}
              </button>
            ))}
            <button
              onClick={() => { setViewWidthFt(totalLengthFt); setViewStation(0); }}
              className={`px-2 py-1 text-xs transition-colors ${viewWidthFt >= totalLengthFt ? 'bg-gray-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              All
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-sm overflow-hidden border border-gray-700">
        <Plot
          data={plotData}
          layout={plotLayout}
          config={plotConfig}
          style={{ width: '100%', height: '300px' }}
          useResizeHandler={true}
        />
      </div>

      {viewWidthFt < totalLengthFt && (
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 w-20">{toHighwayStation(stationOffset)}</span>
          <input
            type="range"
            min={0}
            max={Math.max(0, totalLengthFt - viewWidthFt)}
            value={viewStation}
            onChange={(e) => setViewStation(Number(e.target.value))}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-xs text-gray-500 w-20 text-right">{toHighwayStation(stationOffset + totalLengthFt)}</span>
        </div>
      )}
    </div>
  );
};


// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatStation = (ft) => {
  if (ft == null) return '—';
  const h = Math.floor(ft / 100);
  const r = ft % 100;
  return `${h}+${r.toFixed(0).padStart(2, '0')}`;
};

const formatCurrency = (amount) => {
  return `$${amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

function generateCSV(result) {
  let csv = "Zone ID,Start Station,End Station,Length (ft),Max Cut (in),Avg Cut (in),Triggers\n";
  
  for (const zone of result.grindZones) {
    csv += `${zone.id},${formatStation(zone.systemStartStation)},${formatStation(zone.systemEndStation)},`;
    csv += `${zone.length.toFixed(1)},${zone.maxCut.toFixed(3)},${zone.avgCut.toFixed(3)},`;
    csv += `"${zone.triggers.join('; ')}"\n`;
  }
  
  return csv;
}

function generateReport(result, groundTruth, settings, nonGrindableIssues = []) {
  let report = `${'═'.repeat(70)}\n`;
  report += `                    GRIND SIMULATION REPORT (v4.14)\n`;
  report += `                    Ames Physics Engine - Tandem Geometry\n`;
  report += `${'═'.repeat(70)}\n\n`;

  report += `Generated: ${new Date().toLocaleString()}\n\n`;

  report += `─── MACHINE CONFIGURATION ──────────────────────────────────────────\n`;
  report += `  Machine Class:          ${settings.machineType.toUpperCase()}\n`;
  report += `  Wheelbase:              ${settings.wheelbaseFt} ft\n`;
  report += `  Head Position:          ${(settings.headPosition * 100).toFixed(0)}% from rear\n`;
  report += `  Front Tandem Spread:    ${settings.frontTandemSpreadFt} ft\n`;
  report += `  Rear Tandem Spread:     ${settings.rearTandemSpreadFt} ft\n`;
  report += `  Pre-Filter Cutoff:      ${settings.shortCutoffFt} ft\n\n`;

  report += `─── OPERATIONAL PARAMETERS ─────────────────────────────────────────\n`;
  report += `  Min Zone Length:        ${settings.minZoneLengthFt} ft\n`;
  const reportLeadIn = calculateLeadIn(settings);
  const reportMergeGap = settings.customMergeGap && settings.mergeGapFt ? settings.mergeGapFt : calculateMergeGap(reportLeadIn);
  report += `  Lead-in:                ${reportLeadIn} ft (calculated from geometry)\n`;
  
  // Show merge gap with optimization status
  if (result.optimization?.isOptimized) {
    report += `  Merge Gap:              ${result.optimization.mergeGap} ft (auto-optimized)\n`;
  } else if (settings.customMergeGap) {
    report += `  Merge Gap:              ${reportMergeGap} ft (custom override)\n`;
  } else {
    report += `  Merge Gap:              ${reportMergeGap} ft (default)\n`;
  }
  report += `  Lane Width:             ${settings.laneWidthFt} ft\n\n`;

  report += `─── GROUND TRUTH (FROM ACF) ────────────────────────────────────────\n`;
  report += `  Project MRI:            ${groundTruth.projectMRI.toFixed(2)} in/mi\n`;
  report += `  Total Segments:         ${groundTruth.totalSegments}\n`;
  report += `  Failing Segments:       ${groundTruth.failCount}\n`;
  report += `  ALR Events:             ${groundTruth.alrCount}\n`;
  report += `  Compliance Rate:        ${groundTruth.complianceRate.toFixed(1)}%\n\n`;

  report += `─── SIMULATION RESULTS ─────────────────────────────────────────────\n`;
  report += `  Predicted MRI:          ${result.summary.predictedProjectMRI.toFixed(2)} in/mi\n`;
  report += `  IRI Improvement:        ${result.summary.iriImprovement >= 0 ? '-' : '+'}${Math.abs(result.summary.iriImprovementPercent).toFixed(1)}%\n`;
  report += `  Passes Used:            ${result.totalPassesUsed}\n`;
  report += `  Remaining Failures:     ${result.summary.remainingFailingSegments}\n`;
  report += `  \n`;
  report += `  ALR Coverage:\n`;
  report += `    Original ALRs:        ${result.summary.originalALRCount}\n`;
  report += `    In Grind Zones:       ${result.summary.targetedALRs || 0}\n`;
  report += `    Not Targeted:         ${result.summary.notTargetedALRs || 0} (dips, out of range)\n`;
  report += `  \n`;
  report += `  Predicted Compliance:   ${result.summary.predictedComplianceRate.toFixed(1)}%\n`;
  if (result.summary.iriValidationWarning) {
    report += `  ⚠ IRI Validation:       ${result.summary.iriValidationDiff.toFixed(1)} in/mi diff from ACF\n`;
  }
  report += `\n`;

  report += `─── GRIND PLAN SUMMARY ─────────────────────────────────────────────\n`;
  report += `  Strategy:               ${result.metrics.grindStrategy === 'continuous' ? 'CONTINUOUS GRIND' : 'SPOT GRIND'}\n`;
  report += `  Total Zones:            ${result.grindZones.length}\n`;
  report += `  Total Length:           ${result.metrics.totalGrindLengthFt.toLocaleString()} ft\n`;
  report += `  Total Area:             ${result.metrics.totalAreaSqYd.toLocaleString()} sq yd\n`;
  report += `  Average Cut:            ${result.metrics.avgCutIn.toFixed(3)}" (avg bump height removed)\n`;
  report += `  Maximum Cut:            ${result.metrics.maxCutIn.toFixed(3)}" (largest bump)\n`;
  report += `  Estimated Time:         ~${result.metrics.estimatedHours} hours\n\n`;

  report += `─── COST ESTIMATE ──────────────────────────────────────────────────\n`;
  const cb = result.metrics.costBreakdown;
  report += `  ${result.metrics.totalAreaSqYd.toLocaleString()} SY × $${cb.ratePerSqYd}/SY = $${cb.grinding.toLocaleString()}\n`;
  report += `\n`;

  report += `─── GRIND ZONES ────────────────────────────────────────────────────\n`;
  for (const zone of result.grindZones) {
    report += `\n  ${zone.id}: ${formatStation(zone.systemStartStation)} to ${formatStation(zone.systemEndStation)}\n`;
    report += `      Length: ${zone.length.toFixed(0)} ft\n`;
    report += `      Max Cut: ${zone.maxCut.toFixed(3)}" | Avg Cut: ${zone.avgCut.toFixed(3)}"\n`;
    report += `      Triggers: ${zone.triggers.slice(0, 3).join('; ')}\n`;
  }

  if (nonGrindableIssues && nonGrindableIssues.length > 0) {
    report += `\n─── NON-GRINDABLE ISSUES (Dips/Other) ──────────────────────────────\n`;
    report += `  The following issues were detected but CANNOT be corrected by grinding:\n\n`;
    for (const issue of nonGrindableIssues) {
      if (issue.eventId) {
        report += `  • ALR ${issue.eventId} (${issue.wheelpath}) at ${formatStation(issue.peakStation)}\n`;
        report += `    Magnitude: ${Math.round(issue.magnitude)} in/mi\n`;
      } else if (issue.segmentNumber) {
        report += `  • Segment ${issue.segmentNumber}: ${formatStation(issue.startStation)} to ${formatStation(issue.endStation)}\n`;
        report += `    MRI: ${issue.mri?.toFixed(1)} in/mi\n`;
      }
      report += `    Reason: ${issue.reason}\n`;
      report += `    Recommendation: ${issue.recommendation}\n\n`;
    }
  }

  // Post-grind ALR scan results
  const postGrindList = result.remainingIssues?.postGrindALRs || result.postGrindALRs?.allALRs || [];
  
  if (postGrindList.length > 0) {
    report += `\n─── POST-GRIND ALR SCAN ────────────────────────────────────────────\n`;
    report += `  (Simulates re-running profiler on ground surface)\n\n`;
    
    // Show summary of issue types
    const edgeCount = postGrindList.filter(a => a.relation === 'edge_artifact').length;
    const gapCount = postGrindList.filter(a => a.relation === 'gap_issue').length;
    const dipCount = postGrindList.filter(a => a.relation === 'dip_adjacent').length;
    const uncoveredCount = postGrindList.filter(a => a.relation === 'uncovered' || !a.relation).length;
    
    if (edgeCount > 0 || gapCount > 0 || dipCount > 0) {
      report += `  Issue Breakdown:\n`;
      if (dipCount > 0) report += `    • Dip Adjacent: ${dipCount} (near unfilled dips - grinding bridges over dips)\n`;
      if (edgeCount > 0) report += `    • Edge Artifacts: ${edgeCount} (near zone boundaries - may improve with longer taper)\n`;
      if (gapCount > 0) report += `    • Gap Issues: ${gapCount} (between zones - consider extending coverage)\n`;
      if (uncoveredCount > 0) report += `    • Uncovered Areas: ${uncoveredCount} (outside grind coverage)\n`;
      report += `\n`;
    }
    
    report += `  ID          WP     Peak Station   Magnitude    Length     Cause\n`;
    report += `  ─────────── ────── ────────────── ──────────── ────────── ─────────────\n`;
    
    const relationLabels = {
      'edge_artifact': 'Edge Artifact',
      'gap_issue': 'Gap Issue',
      'dip_adjacent': 'Dip Adjacent',
      'uncovered': 'Uncovered',
      'inside_zone': 'In Zone',
    };
    
    for (const alr of postGrindList) {
      const id = String(alr.id).padEnd(11);
      const wp = alr.wheelpath.padEnd(6);
      const peak = formatStation(alr.peakStation).padEnd(14);
      const mag = `${Math.round(alr.magnitude)} in/mi`.padEnd(12);
      const len = `${alr.length?.toFixed(0) || '-'} ft`.padEnd(10);
      const cause = relationLabels[alr.relation] || 'Unknown';
      
      report += `  ${id} ${wp} ${peak} ${mag} ${len} ${cause}\n`;
    }
  } else {
    report += `\n─── POST-GRIND ALR SCAN ────────────────────────────────────────────\n`;
    report += `  ✓ No ALRs detected above ${settings.alrThreshold} in/mi threshold after grinding.\n`;
  }

  if (result.remainingIssues.segments.length > 0 || result.remainingIssues.alrs.length > 0) {
    report += `\n─── REMAINING ISSUES AFTER GRINDING ────────────────────────────────\n`;
    if (result.remainingIssues.segments.length > 0) {
      report += `  Segments Still Failing:\n`;
      for (const seg of result.remainingIssues.segments) {
        report += `    Segment ${seg.segmentNumber}: ${seg.predictedMRI.toFixed(1)} in/mi (target: ${settings.iriThreshold})\n`;
      }
    }
    if (result.remainingIssues.alrs.length > 0) {
      report += `  ALRs Still Exceeding Threshold:\n`;
      for (const alr of result.remainingIssues.alrs) {
        report += `    ${alr.wheelpath} at ${formatStation(alr.peakStation)}: ${Math.round(alr.predictedMagnitude)} in/mi\n`;
      }
    }
  }

  report += `\n─── TERMINOLOGY ────────────────────────────────────────────────────\n`;
  report += `  • Max Cut / Avg Cut: Height of bumps removed (not plunge depth)\n`;
  report += `  • Simulation planes to ski line, removing all material above reference\n`;
  report += `  • Continuous Grind: Full pass from first to last bump\n`;
  report += `  • Spot Grind: Discrete zones targeting specific bumps\n`;
  
  // Add optimization results table if available
  if (result.optimization?.isOptimized && result.optimization?.candidates) {
    report += `\n─── MERGE GAP OPTIMIZATION ─────────────────────────────────────────\n`;
    report += `  Evaluated ${result.optimization.candidates.length} merge gap candidates with full IRI simulation:\n\n`;
    report += `  Gap (ft)   Zones   Cost        Post-ALRs  Gap      Dip Adj  IRI Δ     Score\n`;
    report += `  ─────────────────────────────────────────────────────────────────────────────\n`;
    
    for (const r of result.optimization.candidates) {
      const isOptimal = r.mergeGap === result.optimization.mergeGap;
      const gap = String(r.mergeGap).padEnd(8);
      const zones = String(r.zoneCount).padEnd(7);
      const cost = `$${r.cost.toLocaleString()}`.padEnd(11);
      const postAlrs = String(r.postGrindAlrs || 0).padEnd(10);
      const gapIssues = String(r.gapIssues || 0).padEnd(8);
      const dipAdj = String(r.dipAdjacent || 0).padEnd(8);
      const iriDelta = `${r.iriImprovement || '0'}%`.padEnd(9);
      const score = r.score.toFixed(0);
      const marker = isOptimal ? ' ← optimal' : '';
      
      report += `  ${gap} ${zones} ${cost} ${postAlrs} ${gapIssues} ${dipAdj} ${iriDelta} ${score}${marker}\n`;
    }
    report += `\n  Score = (Post-ALRs × 50) + (Gap Issues × 150) + (Dip Adj × 25) + (Cost ÷ 100) - (IRI% × 10)\n`;
    report += `  Lower score = better balance of quality and cost\n`;
  }
  
  report += `\n${'═'.repeat(70)}\n`;
  report += `  Report generated by Ames Connect Grind Planner v4.14\n`;
  report += `${'═'.repeat(70)}\n`;

  return report;
}

function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}


// ============================================================================
// MAIN COMPONENT
// ============================================================================

const GrindPlanner = ({ events = [], segments = [], projectConfig = null }) => {

  const iriThreshold = projectConfig?.iri_threshold_type_a || 70;
  const alrThreshold = iriThreshold;
  const settingsButtonRef = useRef(null);

  // State
  const [ppfFile, setPpfFile] = useState(null);
  const [ppfData, setPpfData] = useState(null);
  const [ppfError, setPpfError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [sessionStartStation, setSessionStartStation] = useState('');
  const [parsedStartStation, setParsedStartStation] = useState(null);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [simulationResults, setSimulationResults] = useState(null); // { continuous: result, spot: result }
  const [selectedStrategy, setSelectedStrategy] = useState('continuous');
  const [showStrategyDropdown, setShowStrategyDropdown] = useState(false);
  const [nonGrindableIssues, setNonGrindableIssues] = useState([]);
  const [activeTab, setActiveTab] = useState('plan');
  const [mergeGapOptimization, setMergeGapOptimization] = useState(null); // Optimization results
  const strategyDropdownRef = useRef(null);
  
  // Derived: current result based on selected strategy
  const simulationResult = simulationResults?.[selectedStrategy] || null;

  // Settings with defaults
  const [grindSettings, setGrindSettings] = useState({
    ...DEFAULT_GRIND_SETTINGS,
    iriThreshold,
    alrThreshold,
  });

  // Station parsing
  const handleStationChange = useCallback((value) => {
    setSessionStartStation(value);
    const match = value.match(/^(\d+)\+(\d+\.?\d*)$/);
    if (match) {
      setParsedStartStation(parseInt(match[1]) * 100 + parseFloat(match[2]));
    } else {
      const num = parseFloat(value);
      setParsedStartStation(isNaN(num) ? null : num);
    }
  }, []);

  // Ground truth from ACF
  const groundTruth = useMemo(() => {
    return analyzeGroundTruth(segments, events, grindSettings);
  }, [segments, events, grindSettings]);

  // PPF upload handler
  const handlePPFUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setPpfFile(file);
    setPpfError(null);
    setIsLoading(true);
    setSimulationResults(null);
    setNonGrindableIssues([]);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const parsed = parsePPF(arrayBuffer);

      if (!parsed || parsed.numPoints === 0) {
        throw new Error('PPF file parsed but contains no data points');
      }

      setPpfData(parsed);

      // Auto-set start station from first segment
      if (segments.length > 0 && parsedStartStation === null) {
        const firstSegmentStation = segments[0].from_station_ft;
        if (firstSegmentStation) {
          setParsedStartStation(firstSegmentStation);
          const hundreds = Math.floor(firstSegmentStation / 100);
          const remainder = firstSegmentStation % 100;
          setSessionStartStation(`${hundreds}+${remainder.toFixed(2).padStart(5, '0')}`);
        }
      }
    } catch (err) {
      setPpfError(err.message);
      setPpfData(null);
    } finally {
      setIsLoading(false);
    }
  }, [segments, parsedStartStation]);

  // Run simulation (computes BOTH strategies)
  const runSimulation = useCallback(async () => {
    if (!ppfData) return;

    setIsLoading(true);
    setPpfError(null);
    const profilePoints = ppfData.numPoints.toLocaleString();
    setLoadingMessage(`Analyzing deficient zones (${profilePoints} points)...`);
    
    // Helper to yield to browser for UI updates
    const yieldToMain = () => new Promise(resolve => setTimeout(resolve, 0));

    try {
      await yieldToMain(); // Let UI update with loading message
      
      // Run CONTINUOUS simulation
      setLoadingMessage(`Running Continuous Grind simulation (${profilePoints} points)...`);
      await yieldToMain();
      
      const continuousSettings = { ...grindSettings, grindStrategy: 'continuous' };
      
      const { grindableZones: contZones, nonGrindableIssues: issues, allDipLocations } = identifyDeficientZones(
        groundTruth,
        ppfData,
        parsedStartStation,
        continuousSettings
      );

      setNonGrindableIssues(issues);

      if (contZones.length === 0) {
        if (issues.length > 0) {
          setPpfError(`No grindable zones found. ${issues.length} issue(s) require alternative correction (see report).`);
        } else {
          setPpfError('No deficient zones found in PPF range.');
        }
        setIsLoading(false);
        setLoadingMessage('');
        return;
      }

      const contAligned = alignStations(contZones, ppfData, parsedStartStation);
      
      let continuousResult = null;
      if (contAligned.length > 0) {
        setLoadingMessage(`Simulating Continuous Grind (${contAligned.length} zones)...`);
        await yieldToMain();
        
        continuousResult = runGrindSimulation(
          ppfData,
          contAligned,
          continuousSettings,
          groundTruth,
          parsedStartStation,
          issues,
          allDipLocations
        );
      }

      // Run SPOT simulation with optimized merge gap
      setLoadingMessage(`Optimizing Spot Grind parameters...`);
      await yieldToMain();
      
      // First, get grindable ALRs using base settings
      const baseSpotSettings = { ...grindSettings, grindStrategy: 'spot' };
      const { grindableZones: baseSpotZones, allDipLocations: spotDipLocations } = identifyDeficientZones(
        groundTruth,
        ppfData,
        parsedStartStation,
        { ...baseSpotSettings, customMergeGap: true, mergeGapFt: 10000 } // Use huge merge gap to get all ALRs as one zone
      );
      
      // Extract individual ALR positions from continuous result for optimization
      // We need the raw ALR list, not merged zones
      const grindableAlrs = groundTruth.alrEvents
        .filter(alr => {
          const grindability = checkAlrGrindability(alr, ppfData, parsedStartStation, baseSpotSettings);
          return grindability.grindable;
        })
        .map(alr => ({
          startStation: alr.startStation,
          endStation: alr.endStation,
          peakStation: alr.peakStation,
          eventId: alr.eventId,
        }));
      
      // Determine merge gap candidates to test
      const leadInFt = calculateLeadIn(grindSettings);
      let mergeGapCandidates;
      let optimalMergeGap;
      let optimizationResults = null;
      
      if (grindSettings.customMergeGap) {
        // User override - just use their value
        mergeGapCandidates = [grindSettings.mergeGapFt];
        optimalMergeGap = grindSettings.mergeGapFt;
      } else if (grindableAlrs.length >= 2) {
        // Generate smart candidates based on ALR distribution
        mergeGapCandidates = generateMergeGapCandidates(grindableAlrs, leadInFt);
      } else {
        // Not enough ALRs to optimize
        mergeGapCandidates = [calculateMergeGap(leadInFt)];
        optimalMergeGap = mergeGapCandidates[0];
      }
      
      // Run actual simulations for each candidate (if optimizing)
      let spotResult = null;
      
      if (mergeGapCandidates.length > 1) {
        optimizationResults = [];
        let bestScore = Infinity;
        let bestResult = null;
        
        for (let i = 0; i < mergeGapCandidates.length; i++) {
          const mergeGap = mergeGapCandidates[i];
          setLoadingMessage(`Optimizing: testing ${mergeGap}ft merge gap (${i + 1}/${mergeGapCandidates.length})...`);
          await yieldToMain();
          
          // Run zone planning with this merge gap
          const testSettings = { 
            ...grindSettings, 
            grindStrategy: 'spot',
            customMergeGap: true,
            mergeGapFt: mergeGap,
          };
          
          const { grindableZones: testZones } = identifyDeficientZones(
            groundTruth,
            ppfData,
            parsedStartStation,
            testSettings
          );
          
          const testAligned = alignStations(testZones, ppfData, parsedStartStation);
          
          if (testAligned.length === 0) continue;
          
          // Run full simulation
          const testResult = runGrindSimulation(
            ppfData,
            testAligned,
            testSettings,
            groundTruth,
            parsedStartStation,
            issues,
            allDipLocations
          );
          
          if (!testResult) continue;
          
          // Extract actual post-grind ALRs from simulation result
          const postGrindList = testResult.remainingIssues?.postGrindALRs || [];
          const postGrindAlrCount = postGrindList.length;
          const gapIssueCount = postGrindList.filter(a => a.relation === 'gap_issue' || a.relation === 'gap').length;
          const dipAdjacentCount = postGrindList.filter(a => a.relation === 'dip_adjacent').length;
          
          const cost = testResult.metrics.estimatedCost;
          const iriImprovement = testResult.summary.iriImprovementPercent;
          
          // Score: prioritize fewer post-grind ALRs and gap issues
          // Dip-adjacent issues are less penalized since they require patching regardless
          const score = (postGrindAlrCount * 50) + (gapIssueCount * 150) + (dipAdjacentCount * 25) + (cost / 100) - (iriImprovement * 10);
          
          optimizationResults.push({
            mergeGap,
            zoneCount: testResult.grindZones.length,
            totalLength: testResult.metrics.totalLength,
            areaSqYd: testResult.metrics.totalAreaSqYd,
            cost,
            postGrindAlrs: postGrindAlrCount,
            gapIssues: gapIssueCount,
            dipAdjacent: dipAdjacentCount,
            iriImprovement: iriImprovement.toFixed(1),
            score,
            result: testResult,
          });
          
          if (score < bestScore) {
            bestScore = score;
            bestResult = testResult;
            optimalMergeGap = mergeGap;
          }
        }
        
        // Sort results by score
        optimizationResults.sort((a, b) => a.score - b.score);
        
        spotResult = bestResult;
      } else {
        // Single candidate - just run it
        const spotSettings = { 
          ...grindSettings, 
          grindStrategy: 'spot',
          customMergeGap: true,
          mergeGapFt: mergeGapCandidates[0],
        };
        
        setLoadingMessage(`Running Spot Grind simulation (merge gap: ${spotSettings.mergeGapFt}ft)...`);
        await yieldToMain();
        
        const { grindableZones: spotZones } = identifyDeficientZones(
          groundTruth,
          ppfData,
          parsedStartStation,
          spotSettings
        );
        
        const spotAligned = alignStations(spotZones, ppfData, parsedStartStation);
        
        if (spotAligned.length > 0) {
          spotResult = runGrindSimulation(
            ppfData,
            spotAligned,
            spotSettings,
            groundTruth,
            parsedStartStation,
            issues,
            allDipLocations
          );
        }
        
        optimalMergeGap = mergeGapCandidates[0];
      }
      
      // Attach optimization info to spot result
      if (spotResult) {
        spotResult.optimization = {
          mergeGap: optimalMergeGap,
          isOptimized: !grindSettings.customMergeGap && mergeGapCandidates.length > 1,
          candidates: optimizationResults,
        };
      }
      
      // Store optimization results for UI
      setMergeGapOptimization(optimizationResults);

      // Store both results
      setSimulationResults({
        continuous: continuousResult,
        spot: spotResult,
      });
      
      // Default to continuous if available, else spot
      if (continuousResult) {
        setSelectedStrategy('continuous');
      } else if (spotResult) {
        setSelectedStrategy('spot');
      }

      setLoadingMessage('');

    } catch (err) {
      console.error('Simulation error:', err);
      setPpfError(`Simulation failed: ${err.message}`);
      setLoadingMessage('');
    } finally {
      setIsLoading(false);
    }
  }, [ppfData, parsedStartStation, groundTruth, grindSettings]);

  // Clear handler
  const handleClear = useCallback(() => {
    setPpfData(null);
    setPpfFile(null);
    setSimulationResults(null);
    setNonGrindableIssues([]);
    setPpfError(null);
    setLoadingMessage('');
  }, []);
  
  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (strategyDropdownRef.current && !strategyDropdownRef.current.contains(e.target)) {
        setShowStrategyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">Grind Planner</h3>
            <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">v4.14</span>
          </div>

          <div className="flex items-center space-x-2">
            {ppfData && (
              <>
                <span className="text-xs text-gray-500 max-w-48 truncate">{ppfFile?.name}</span>
                <span className="text-xs text-gray-400">{ppfData.totalLengthFt.toFixed(0)} ft</span>
                <div className="h-4 w-px bg-gray-300" />
                <div className="flex items-center space-x-1.5">
                  <label className="text-xs text-gray-500">Start:</label>
                  <input
                    type="text"
                    value={sessionStartStation}
                    onChange={(e) => handleStationChange(e.target.value)}
                    placeholder="125+00"
                    className="px-2 py-1 text-xs border border-gray-300 rounded w-24 font-mono"
                  />
                  {parsedStartStation !== null && <CheckCircleIcon className="h-3.5 w-3.5 text-green-500" />}
                </div>
              </>
            )}

            {ppfData && !simulationResults && (
              <button
                onClick={runSimulation}
                disabled={isLoading || parsedStartStation === null}
                className="inline-flex items-center px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Spinner size={14} className="mr-1.5" />
                    <span className="text-xs">{loadingMessage || 'Processing...'}</span>
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-3.5 w-3.5 mr-1" />
                    Simulate
                  </>
                )}
              </button>
            )}

            {simulationResults && (
              <>
                {/* Strategy Dropdown */}
                <div className="relative" ref={strategyDropdownRef}>
                  <button
                    onClick={() => setShowStrategyDropdown(!showStrategyDropdown)}
                    className="inline-flex items-center justify-between px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 min-w-[160px]"
                  >
                    <span className="flex items-center">
                      <span className={`w-2 h-2 rounded-full mr-2 ${selectedStrategy === 'continuous' ? 'bg-blue-500' : 'bg-purple-500'}`} />
                      {selectedStrategy === 'continuous' ? 'Continuous Grind' : 'Spot Grind'}
                    </span>
                    <ChevronDownIcon className={`h-4 w-4 ml-2 text-gray-400 transition-transform ${showStrategyDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showStrategyDropdown && (
                    <div className="absolute right-0 mt-1 w-72 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                      <div className="py-1">
                        <button
                          onClick={() => { setSelectedStrategy('continuous'); setShowStrategyDropdown(false); }}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-start ${selectedStrategy === 'continuous' ? 'bg-blue-50' : ''}`}
                          disabled={!simulationResults.continuous}
                        >
                          <span className={`w-2 h-2 rounded-full mt-1.5 mr-2 ${simulationResults.continuous ? 'bg-blue-500' : 'bg-gray-300'}`} />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">Continuous Grind</div>
                            <div className="text-xs text-gray-500">Full pass, best IRI improvement</div>
                            {simulationResults.continuous && (
                              <div className="text-xs text-green-600 mt-0.5">
                                {formatCurrency(simulationResults.continuous.metrics.estimatedCost)} • -{simulationResults.continuous.summary.iriImprovementPercent.toFixed(0)}% IRI
                              </div>
                            )}
                          </div>
                          {selectedStrategy === 'continuous' && <CheckIcon className="h-4 w-4 text-blue-600 ml-2" />}
                        </button>
                        
                        <button
                          onClick={() => { setSelectedStrategy('spot'); setShowStrategyDropdown(false); }}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-start ${selectedStrategy === 'spot' ? 'bg-purple-50' : ''}`}
                          disabled={!simulationResults.spot}
                        >
                          <span className={`w-2 h-2 rounded-full mt-1.5 mr-2 ${simulationResults.spot ? 'bg-purple-500' : 'bg-gray-300'}`} />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 flex items-center gap-1.5">
                              Spot Grind
                              {simulationResults.spot?.optimization?.isOptimized && (
                                <span className="text-[10px] px-1 py-0.5 bg-green-100 text-green-700 rounded">optimized</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              {simulationResults.spot?.optimization 
                                ? `${simulationResults.spot.grindZones.length} zones • ${simulationResults.spot.optimization.mergeGap}ft merge gap`
                                : 'Discrete zones, lower material'}
                            </div>
                            {simulationResults.spot && (
                              <div className="text-xs text-green-600 mt-0.5">
                                {formatCurrency(simulationResults.spot.metrics.estimatedCost)} • -{simulationResults.spot.summary.iriImprovementPercent.toFixed(0)}% IRI
                              </div>
                            )}
                          </div>
                          {selectedStrategy === 'spot' && <CheckIcon className="h-4 w-4 text-purple-600 ml-2" />}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="h-4 w-px bg-gray-300" />
                <button
                  onClick={() => downloadFile(generateCSV(simulationResult), 'grind_plan.csv', 'text/csv')}
                  className="inline-flex items-center px-2 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                  CSV
                </button>
                <button
                  onClick={() => downloadFile(
                    generateReport(simulationResult, groundTruth, { ...grindSettings, grindStrategy: selectedStrategy }, nonGrindableIssues),
                    'grind_report.txt',
                    'text/plain'
                  )}
                  className="inline-flex items-center px-2 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                  Report
                </button>
                <div className="h-4 w-px bg-gray-300" />
                <button
                  ref={settingsButtonRef}
                  onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                  className={`inline-flex items-center px-2 py-1 text-sm bg-white border border-gray-300 rounded ${showSettingsDropdown ? 'bg-gray-200' : 'hover:bg-gray-50'}`}
                >
                  <Cog6ToothIcon className="h-4 w-4 mr-1" />
                  Settings
                </button>
                <button
                  onClick={runSimulation}
                  disabled={isLoading}
                  className="inline-flex items-center px-2 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                  title="Re-run simulation"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
              </>
            )}

            {ppfData && (
              <button
                onClick={handleClear}
                className="inline-flex items-center font-bold px-2 py-1 text-sm bg-red-500 border border-red-500 rounded text-white hover:bg-red-600"
              >
                <XCircleIcon className="h-4 w-4 mr-1" />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Settings Dropdown */}
      <SettingsDropdown
        isOpen={showSettingsDropdown}
        onClose={() => setShowSettingsDropdown(false)}
        anchorRef={settingsButtonRef}
        grindSettings={grindSettings}
        setGrindSettings={setGrindSettings}
      />

      {/* Upload prompt */}
      {!ppfData && (
        <div className="p-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              accept=".ppf"
              onChange={handlePPFUpload}
              className="hidden"
              id="ppf-upload"
            />
            <label htmlFor="ppf-upload" className="cursor-pointer">
              <ArrowUpTrayIcon className="h-10 w-10 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-700">Upload PPF file for grind simulation</p>
              <p className="text-xs text-gray-500 mt-1">
                Uses ACF ground truth for defect locations, PPF for profile physics
              </p>
            </label>
          </div>
        </div>
      )}

      {/* Error display */}
      {ppfError && (
        <div className="mx-4 mt-2 p-3 bg-red-50 border border-red-200 rounded">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-700">{ppfError}</p>
              {nonGrindableIssues.length > 0 && (
                <p className="text-xs text-red-600 mt-1">
                  {nonGrindableIssues.length} non-grindable issue(s) detected. Download report for details.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay during re-simulation */}
      {isLoading && simulationResults && (
        <div className="mx-4 mt-2 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex flex-col items-center justify-center space-y-2">
            <div className="flex items-center">
              <ArrowPathIcon className="h-5 w-5 animate-spin text-blue-600 mr-2" />
              <span className="text-sm text-blue-700">{loadingMessage || 'Re-running simulation...'}</span>
            </div>
            <div className="w-48 h-1.5 bg-blue-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{ width: '100%' }} />
            </div>
          </div>
        </div>
      )}
      
      {/* Loading overlay during initial simulation (before results exist) */}
      {isLoading && !simulationResults && ppfData && (
        <div className="mx-4 mt-4 p-6 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex flex-col items-center justify-center space-y-3">
            <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-600" />
            <span className="text-sm font-medium text-blue-700">{loadingMessage || 'Processing...'}</span>
            <div className="w-64 h-2 bg-blue-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{ width: '100%' }} />
            </div>
            <span className="text-xs text-blue-500">Large profiles may take 30-60 seconds</span>
          </div>
        </div>
      )}

      {/* Results */}
      {simulationResult && (
        <>
          {/* Summary bar */}
          <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between text-sm">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <span className="text-gray-600">{simulationResult.summary.originalProjectMRI.toFixed(1)}</span>
                <span className="mx-1.5 text-gray-400">→</span>
                <span className="font-semibold text-green-600">{simulationResult.summary.predictedProjectMRI.toFixed(1)}</span>
                <span className="ml-1 text-xs text-gray-500">in/mi</span>
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                  -{simulationResult.summary.iriImprovementPercent.toFixed(0)}%
                </span>
              </div>
              <div className="h-4 w-px bg-gray-300" />
              {/* Grind Mode with explanation */}
              <div className="flex items-center gap-1.5" title={
                simulationResult.metrics.grindStrategy === 'continuous' 
                  ? 'Continuous Grind: Full pass from first to last bump. Best IRI results.' 
                  : 'Spot Grind: Discrete zones targeting specific bumps. Lower material use.'
              }>
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                  simulationResult.metrics.grindStrategy === 'continuous' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-purple-100 text-purple-700'
                }`}>
                  {simulationResult.metrics.grindStrategy === 'continuous' ? 'Continuous' : 'Spot'}
                </span>
                <span className="text-xs text-gray-500">
                  {simulationResult.metrics.grindStrategy === 'continuous' 
                    ? '(full pass)' 
                    : `(${simulationResult.grindZones.length} zones)`}
                </span>
              </div>
              <div className="h-4 w-px bg-gray-300" />
              <span className="text-green-600 font-medium">
                {formatCurrency(simulationResult.metrics.estimatedCost)}
              </span>
            </div>
            <div className="flex items-center space-x-2 text-xs">
              {simulationResult.summary.remainingFailingSegments > 0 ? (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded">
                  {simulationResult.summary.remainingFailingSegments} failing
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">All pass</span>
              )}
              {simulationResult.summary.iriValidationWarning && (
                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                  IRI diff: {simulationResult.summary.iriValidationDiff.toFixed(1)}
                </span>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex">
              {['plan', 'segments', 'alrs', 'map'].map(id => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === id
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  {id.charAt(0).toUpperCase() + id.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          <div className="p-4">
            {activeTab === 'plan' && (
              <div className="space-y-4">
                <ProfileChart
                  ppfData={ppfData}
                  simulationResult={simulationResult}
                  grindZones={simulationResult.grindZones}
                  sessionStartStation={parsedStartStation}
                />

                {/* Zones table */}
                <div className="overflow-x-auto border border-gray-200 rounded-sm">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Zone</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">From</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">To</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Length</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Max Cut</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Avg Cut</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Triggers</th>
                        <th className="px-3 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {simulationResult.grindZones.map(zone => (
                        <tr key={zone.id} className={selectedZoneId === zone.id ? 'bg-blue-50' : ''}>
                          <td className="px-3 py-2 font-medium">{zone.id}</td>
                          <td className="px-3 py-2 font-mono text-gray-600">{formatStation(zone.systemStartStation)}</td>
                          <td className="px-3 py-2 font-mono text-gray-600">{formatStation(zone.systemEndStation)}</td>
                          <td className="px-3 py-2">{zone.length.toFixed(0)} ft</td>
                          <td className="px-3 py-2">{zone.maxCut.toFixed(3)}"</td>
                          <td className="px-3 py-2">{zone.avgCut.toFixed(3)}"</td>
                          <td className="px-3 py-2 text-xs text-gray-500 max-w-48 truncate" title={zone.triggers.join('; ')}>
                            {zone.triggers.slice(0, 2).join('; ')}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => { setSelectedZoneId(zone.id); setActiveTab('map'); }}
                              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                              title="View on map"
                            >
                              <MapIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'segments' && (
              <div className="border border-gray-200 rounded-sm overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-700">Seg</th>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-700">From</th>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-700">To</th>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-700">Length</th>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-700 border-l border-gray-200">Before</th>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-700 border-l border-gray-200">After</th>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-700 border-l border-gray-200">Δ%</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {simulationResult.segmentResults.map(seg => {
                      const improved = seg.improvement > 1;
                      const wasFailing = seg.status === 'fail';
                      const willFail = seg.predictedMRI > grindSettings.iriThreshold;
                      return (
                        <tr key={seg.segmentNumber} className={!seg.inRange ? 'opacity-50' : ''}>
                          <td className="px-2 py-1 font-medium">{seg.segmentNumber}</td>
                          <td className="px-2 py-1 font-mono text-gray-500">{formatStation(seg.startStation)}</td>
                          <td className="px-2 py-1 font-mono text-gray-500">{formatStation(seg.endStation)}</td>
                          <td className="px-2 py-1 text-gray-500">{seg.length.toFixed(0)} ft</td>
                          <td className={`px-2 py-1 border-l border-gray-200 ${wasFailing ? 'bg-red-100 text-red-700' : ''}`}>
                            {seg.mri.toFixed(1)}
                          </td>
                          <td className={`px-2 py-1 border-l border-gray-200 ${willFail ? 'bg-red-100 text-red-700' : improved ? 'text-green-600' : ''}`}>
                            {seg.predictedMRI.toFixed(1)}
                          </td>
                          <td className="px-2 py-1 border-l border-gray-200 text-green-600">
                            {improved ? `-${seg.improvementPercent.toFixed(0)}%` : ''}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'alrs' && (
              <div className="space-y-4">
                {/* Coverage Summary - Button Group Style */}
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-1.5">Grind Coverage</div>
                  <div className="inline-flex rounded-md shadow-sm">
                    <div className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-l-md text-center min-w-[100px]">
                      <div className="text-xl font-bold text-gray-900">{simulationResult.summary.originalALRCount}</div>
                      <div className="text-[10px] text-gray-500">Original</div>
                    </div>
                    <div className="px-4 py-2 bg-green-50 border-t border-b border-r border-green-300 text-center min-w-[100px]">
                      <div className="text-xl font-bold text-green-700">{simulationResult.summary.targetedALRs || 0}</div>
                      <div className="text-[10px] text-green-600">In Zones</div>
                    </div>
                    <div className="px-4 py-2 bg-amber-50 border-t border-b border-r border-amber-300 rounded-r-md text-center min-w-[100px]">
                      <div className="text-xl font-bold text-amber-700">{simulationResult.summary.notTargetedALRs || 0}</div>
                      <div className="text-[10px] text-amber-600">Not Targeted</div>
                    </div>
                  </div>
                </div>
                
                {/* Post-grind scan results - Neutral styling */}
                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700">
                      Post-Grind Scan
                      <span className="ml-2 text-xs font-normal text-gray-500">(simulated re-profile)</span>
                    </h4>
                  </div>
                  
                  <div className="p-3">
                    {(() => {
                      const postList = simulationResult.remainingIssues?.postGrindALRs || simulationResult.postGrindALRs?.allALRs || [];
                      
                      if (postList.length === 0) {
                        return (
                          <div className="flex items-center text-sm text-green-700">
                            <CheckCircleIcon className="h-5 w-5 mr-2" />
                            No defects detected after grinding
                          </div>
                        );
                      }
                      
                      const edgeCount = postList.filter(a => a.relation === 'edge_artifact').length;
                      const gapCount = postList.filter(a => a.relation === 'gap_issue').length;
                      const dipCount = postList.filter(a => a.relation === 'dip_adjacent').length;
                      const otherCount = postList.length - edgeCount - gapCount - dipCount;
                      
                      return (
                        <>
                          {/* Issue breakdown - inline badges */}
                          <div className="flex items-center flex-wrap gap-2 mb-3">
                            <span className="text-sm font-semibold text-gray-700">{postList.length} remaining:</span>
                            {dipCount > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                                {dipCount} dip-adjacent
                              </span>
                            )}
                            {edgeCount > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-orange-100 text-orange-700">
                                {edgeCount} edge
                              </span>
                            )}
                            {gapCount > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">
                                {gapCount} gap
                              </span>
                            )}
                            {otherCount > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-200 text-gray-700">
                                {otherCount} other
                              </span>
                            )}
                          </div>
                          
                          {/* Defect table */}
                          <div className="border border-gray-200 rounded-sm overflow-hidden">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                  <th className="px-2 py-1.5 text-left font-medium text-gray-600">ID</th>
                                  <th className="px-2 py-1.5 text-left font-medium text-gray-600">WP</th>
                                  <th className="px-2 py-1.5 text-left font-medium text-gray-600">Station</th>
                                  <th className="px-2 py-1.5 text-left font-medium text-gray-600">Magnitude</th>
                                  <th className="px-2 py-1.5 text-left font-medium text-gray-600">Cause</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {postList.map((alr, idx) => {
                                  const causeColors = {
                                    'edge_artifact': 'bg-orange-100 text-orange-700',
                                    'gap_issue': 'bg-red-100 text-red-700',
                                    'dip_adjacent': 'bg-blue-100 text-blue-700',
                                    'inside_zone': 'bg-gray-100 text-gray-600',
                                    'uncovered': 'bg-gray-100 text-gray-600',
                                  };
                                  const causeLabels = {
                                    'edge_artifact': 'Edge',
                                    'gap_issue': 'Gap',
                                    'dip_adjacent': 'Dip Adjacent',
                                    'inside_zone': 'In Zone',
                                    'uncovered': 'Uncovered',
                                  };
                                  
                                  return (
                                    <tr key={idx} className="hover:bg-gray-50">
                                      <td className="px-2 py-1.5 font-medium text-gray-900">{alr.id}</td>
                                      <td className="px-2 py-1.5 text-gray-600">{alr.wheelpath}</td>
                                      <td className="px-2 py-1.5 font-mono text-gray-500">{formatStation(alr.peakStation)}</td>
                                      <td className="px-2 py-1.5 text-gray-900">{Math.round(alr.magnitude)} in/mi</td>
                                      <td className="px-2 py-1.5">
                                        {alr.relation && (
                                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${causeColors[alr.relation] || 'bg-gray-100'}`}>
                                            {causeLabels[alr.relation] || alr.relation}
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'map' && (
              <GrindZoneMapTab
                grindZones={simulationResult.grindZones}
                segments={segments}
                events={events}
                selectedZoneId={selectedZoneId}
                onZoneSelect={setSelectedZoneId}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GrindPlanner;
